/* ============================================================
   IMANUEL — page behavior (vanilla JS, no dependencies)
   Nav solidify · scroll progress · reveal-on-scroll · mega menu
   bundle pricing · carousel · quick add · cart · search · toast
   ============================================================ */

const $  = (s, el = document) => el.querySelector(s);
const $$ = (s, el = document) => [...el.querySelectorAll(s)];

/* ---------- i18n ----------
   Translations are injected by snippets/imanuel-i18n.liquid via Liquid's
   `t` filter (so they follow the storefront's active language). Every lookup
   falls back to the exact English original, so a missing/failed key can never
   break the UI or regress English. `tt()` also rejects Shopify's
   "translation missing:" sentinel. `tf()` fills {name} placeholders. */
const I18N = (typeof window !== "undefined" && window.IMANUEL_I18N) || {};
const tt = (val, fallback) =>
  (typeof val === "string" && val && val.indexOf("translation missing") !== 0) ? val : fallback;
const tf = (val, fallback, vars) =>
  tt(val, fallback).replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] != null ? vars[k] : ""));
const I18Nb = () => I18N.bundle || {};
const I18Nc = () => I18N.cart || {};
const money = (n) =>
  (window.imanuelDisplayCurrency?.formatUsdAmount?.(n))
  || ("$" + n.toLocaleString("en-US"));
/** Format a dollar amount that may carry cents (e.g. upsell-discounted prices). @param {number} n */
const moneyAmt = (n) =>
  (window.imanuelDisplayCurrency?.formatUsdAmount?.(n))
  || (Number.isInteger(n)
    ? money(n)
    : ("$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })));

/* ---------- state ---------- */
const moneyFromCents = (c) =>
  (window.imanuelDisplayCurrency?.formatUsdCents?.(c))
  || ("$" + (c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

async function fetchShopifyCart() {
  const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("Could not load cart");
  return res.json();
}

async function shopifyAddItems(items) {
  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ items }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.description || data.message || "Could not add to bag");
  return data;
}

async function shopifyChangeLine(idOrLine, quantity) {
  const payload = typeof idOrLine === "number"
    ? { line: idOrLine, quantity }
    : { id: idOrLine, quantity };
  const res = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.description || data.message || "Could not update bag");
  return data;
}

function cartLineKey(item) {
  return item?.key || (item?.id != null ? String(item.id) : "");
}

async function removeBundleFromCart(keys) {
  const wanted = new Set(keys.filter(Boolean));
  if (!wanted.size) throw new Error("Could not find bundle in bag");

  let cart = await fetchShopifyCart();
  let targets = cart.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => wanted.has(cartLineKey(item)))
    .sort((a, b) => b.index - a.index);

  if (!targets.length) {
    cart = await fetchShopifyCart();
    targets = cart.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => wanted.has(String(item.id)))
      .sort((a, b) => b.index - a.index);
  }

  if (!targets.length) throw new Error("Could not find bundle in bag");

  for (const { item, index } of targets) {
    const id = cartLineKey(item);
    try {
      await shopifyChangeLine(id, 0);
    } catch {
      await shopifyChangeLine(index + 1, 0);
    }
  }
}

/* ---------- scroll: progress bar + solid nav ---------- */
const nav = $("#nav"), progress = $("#progress");
addEventListener("scroll", () => {
  const max = document.documentElement.scrollHeight - innerHeight;
  if (progress) progress.style.width = (max > 0 ? (scrollY / max) * 100 : 0) + "%";
  if (nav) nav.classList.toggle("is-solid", scrollY > 40);
}, { passive: true });

/* ---------- reveal on scroll (replays when entering from up or down) ---------- */
const io = new IntersectionObserver((entries) => {
  entries.forEach((en) => {
    if (en.isIntersecting) {
      // Force a clean re-entry for staggered about text
      en.target.classList.remove("in");
      // Next frame so the remove takes effect before re-adding
      requestAnimationFrame(() => {
        requestAnimationFrame(() => en.target.classList.add("in"));
      });
    } else {
      en.target.classList.remove("in");
    }
  });
}, { threshold: 0.18, rootMargin: "0px 0px -8% 0px" });
$$(".rv").forEach((el) => io.observe(el));

/* ---------- community (mobile diagonal animated lanes) ---------- */
function initCommunityDiagonalLanes() {
  const mobile = matchMedia("(max-width: 820px)").matches;
  $$(".community").forEach((root) => {
    if (!mobile) {
      if (root.dataset.communityOriginal) {
        root.innerHTML = root.dataset.communityOriginal;
        root.classList.remove("community--diag");
        delete root.dataset.communityMode;
      }
      return;
    }

    if (root.dataset.communityMode === "diag") return;
    if (!root.dataset.communityOriginal) root.dataset.communityOriginal = root.innerHTML;

    const seed = Array.from(root.querySelectorAll(".community__item")).map((el) =>
      el.outerHTML.replace(/\srv\b/g, "").replace(/\sin\b/g, "")
    );
    if (!seed.length) return;

    const laneCount = 4;
    const lanes = Array.from({ length: laneCount }, () => []);
    seed.forEach((item, idx) => lanes[idx % laneCount].push(item));
    const all = seed.slice();

    root.innerHTML = "";
    root.classList.add("community--diag");
    root.dataset.communityMode = "diag";

    for (let i = 0; i < laneCount; i++) {
      const lane = document.createElement("div");
      lane.className = "community__lane";
      const track = document.createElement("div");
      track.className = "community__track";
      track.style.setProperty("--dur", `${16 + i * 2}s`);

      const rowItems = lanes[i].length ? lanes[i] : all;
      const repeated = [];
      while (repeated.length < 6) {
        repeated.push(rowItems[repeated.length % rowItems.length]);
      }
      track.innerHTML = repeated.join("") + repeated.join("");
      lane.appendChild(track);
      root.appendChild(lane);
    }
  });
}

initCommunityDiagonalLanes();
addEventListener("resize", (() => {
  let t;
  return () => {
    clearTimeout(t);
    t = setTimeout(initCommunityDiagonalLanes, 180);
  };
})());

/* ---------- magnetic buttons ---------- */
$$("[data-magnetic]").forEach(el => {
  el.addEventListener("mousemove", e => {
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - r.left - r.width / 2) / r.width;
    const dy = (e.clientY - r.top - r.height / 2) / r.height;
    el.style.transform = `translate(${(dx * 8).toFixed(1)}px, ${(dy * 6).toFixed(1)}px)`;
  });
  el.addEventListener("mouseleave", () => (el.style.transform = ""));
});

/* ---------- mega drawers (Shop, Bundles) ---------- */
const megaPanels = () => $$(".mega");
function anyMegaOpen() {
  return megaPanels().some((m) => !m.hidden);
}
function hideMega(panel, trigger) {
  if (!panel) return;
  panel.hidden = true;
  trigger?.setAttribute("aria-expanded", "false");
  if (!anyMegaOpen()) nav.classList.remove("is-mega");
}
/** Hover/focus drawer. Opening one closes the other so they never stack. */
function bindMega(trigger, panel) {
  if (!trigger || !panel) return;
  let timer;
  const open = () => {
    clearTimeout(timer);
    megaPanels().forEach((m) => {
      if (m !== panel && !m.hidden) hideMega(m, m.id === "mega" ? $("#shop-link") : $("#bundles-link"));
    });
    panel.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    nav.classList.add("is-mega");
  };
  const close = () => {
    clearTimeout(timer);
    timer = setTimeout(() => hideMega(panel, trigger), 160);
  };
  trigger.addEventListener("mouseenter", open);
  trigger.addEventListener("mouseleave", close);
  trigger.addEventListener("focus", open);
  panel.addEventListener("mouseenter", open);
  panel.addEventListener("mouseleave", close);
  panel.addEventListener("focusout", (e) => {
    if (!panel.contains(e.relatedTarget)) close();
  });
  panel.addEventListener("click", () => hideMega(panel, trigger));
}
bindMega($("#shop-link"), $("#mega"));
bindMega($("#bundles-link"), $("#mega-bundles"));
addEventListener("keydown", (e) => {
  if (e.key !== "Escape" || !anyMegaOpen()) return;
  hideMega($("#mega"), $("#shop-link"));
  hideMega($("#mega-bundles"), $("#bundles-link"));
});

/* ---------- bundle & save (multi-product UI, per-product pricing) ---------- */
/*
 * Per-piece bundle pricing, keyed by the product's single-piece price in
 * dollars: $45 → $38 each at 3+, $38 → $32 each at 3+. Must mirror the
 * product-scoped "Bundle & Save" automatic discounts in Shopify admin —
 * the theme only displays these prices, the discounts actually apply them.
 */
/** @type {Record<number, Record<number, number>>} */
const BUNDLE_PRICE_RULES = {
  48: { 3: 40 },
  45: { 3: 38 },
  38: { 3: 32 },
  25: { 3: 22, 6: 18 }, // Tank + men sleeveless
  20: { 3: 18, 6: 16 }, // Women sleeveless
};

/* Product-specific tier overrides (legacy pricing). */
/** @type {Record<number, Record<number, number>>} */
const BUNDLE_PRODUCT_RULES = {
  8634781073583: { 2: 30 }, // Basic Striped Pants — $35 → 2 for $60 ($30 each)
  8610168864943: { 3: 25, 6: 20 }, // Regular Fit T-shirt — $30 → $25 / $20 each
  8622198096047: { 3: 25, 6: 20 }, // Relaxed Fit T-shirt — $30 → $25 / $20 each
  8615898808495: { 3: 22, 6: 18 }, // Oversized Tank Top — $25 → $22 / $18 each
  8623369846959: { 3: 22, 6: 18 }, // Men Sleeveless Top — $25 → $22 / $18 each
  8623384199343: { 3: 18, 6: 16 }, // Women Sleeveless Top — $20 → $18 / $16 each
};

/* Products covered by a "Bundle & Save" automatic discount in Shopify admin.
   Add product IDs here when new product-scoped discounts are created.
   Drives the in-cart savings nudge. */
const BUNDLE_TIER_PRODUCT_IDS = new Set([
  8634781073583, // Basic Striped Pants — $35 → $30 each at 2+
  8610168864943, // Regular Fit T-shirt — $30 → $25 / $20 each
  8622198096047, // Relaxed Fit T-shirt — $30 → $25 / $20 each
  8615445168303, // Basic Hoodie — $45 → $38 each at 3+
  8615450640559, // Basic Zip-up — $48 → $40 each at 3+
  8615454376111, // Basic Quarter Zip — $38 → $32 each at 3+
  8615472693423, // Basic Polo Sweater — $38 → $32 each at 3+
  8615474888879, // Basic Pants — $38 → $32 each at 3+
  8615476789423, // Basic Wideleg Pants — $38 → $32 each at 3+
  8619401150639, // Otello Formal Pants — $38 → $32 each at 3+
  8615898808495, // Oversized Tank Top — $25 → $22 / $18 each
  8623369846959, // Men Sleeveless Top — $25 → $22 / $18 each
  8623384199343, // Women Sleeveless Top — $20 → $18 / $16 each
]);

/**
 * Bundle tiers (sorted pack quantities) a product's price rule defines.
 * @param {number|string|undefined} productId
 * @param {number} base - single-piece price in dollars
 */
function bundleRuleTiers(productId, base) {
  const rule = BUNDLE_PRODUCT_RULES[Number(productId)] || BUNDLE_PRICE_RULES[base];
  return rule ? Object.keys(rule).map(Number).sort((a, b) => a - b) : [];
}

/**
 * Discounted per-piece price for a product at a pack quantity.
 * @param {number|string|undefined} productId
 * @param {number} base - single-piece price in dollars
 * @param {number} qty
 */
function bundleEachPrice(productId, base, qty) {
  if (qty <= 1) return base;
  const rule = BUNDLE_PRODUCT_RULES[Number(productId)] || BUNDLE_PRICE_RULES[base];
  return (rule && rule[qty]) || base;
}

/**
 * Tier pricing (pack price / compare-at / per-piece / savings) for a bundle
 * product, all in dollars. Cart-rail suggestions are full price.
 * @param {any} product - normalized bundle product (needs id + price_cents)
 * @param {number} qty
 */
function bundleTierFor(product, qty) {
  const base = Math.round((product?.price_cents || 0) / 100);
  const each = bundleEachPrice(product?.id, base, qty);
  return {
    qty,
    base,
    each,
    price: Math.round(each * qty * 100) / 100,
    compare: base * qty,
    save: Math.max(0, Math.round((base - each) * qty * 100) / 100),
  };
}

/* ---------- cart upsell rail (product suggestions, no discount) ---------- */
/** Handles always offered in the bag rail when not already in cart. */
function upsellHandleSet() {
  const cfg = cartConversionConfig();
  return new Set([
    ...(cfg.upsellHandles || []),
    ...(cfg.upsellFallbackHandles || []),
  ].filter(Boolean));
}

/** Numeric product IDs from the merchant upsell collection. */
function upsellIdSet() {
  return new Set((cartConversionConfig().upsellIds || []).map(Number).filter(Boolean));
}

/**
 * Whether a product (by id, handle, or url) is in the upsell suggestion set.
 * @param {any} product
 */
function isUpsellPick(product) {
  if (upsellIdSet().has(Number(product?.id))) return true;
  const fromUrl = String(product?.url || "").split("/products/")[1];
  const handle = product?.handle || (fromUrl ? fromUrl.split(/[/?#]/)[0] : null);
  return !!(handle && upsellHandleSet().has(handle));
}

/**
 * Animated before/after price pair (kept for any non-upsell deal UI).
 * @param {number} origCents
 * @param {number} newCents
 */
function dealPriceHtml(origCents, newCents) {
  return `<span class="deal-price"><s>${moneyFromCents(origCents)}</s><b>${moneyFromCents(newCents)}</b></span>`;
}

/** @type {any} */
let BUNDLE_PRODUCT = null;
let BUNDLE_PRODUCTS = new Map();
try {
  const catalogEl = $("#bundle-catalog");
  if (catalogEl) {
    const parsed = JSON.parse(catalogEl.textContent || "{}");
    BUNDLE_PRODUCT = parsed.product || null;
    if (BUNDLE_PRODUCT?.handle) BUNDLE_PRODUCTS.set(BUNDLE_PRODUCT.handle, BUNDLE_PRODUCT);
  }
} catch (_) {
  BUNDLE_PRODUCT = null;
}

function getAllowedBundleQuantities(product) {
  // A product-specific rule defines its own pack sizes (e.g. the Striped Pants
  // 2-pack), so it wins over the color-count heuristic below.
  const ruleTiers = Object.keys(BUNDLE_PRODUCT_RULES[Number(product?.id)] || {}).map(Number);
  if (ruleTiers.length) return [1, ...ruleTiers.sort((a, b) => a - b)];
  const colorCount = (product?.options?.colors || []).length;
  if (colorCount >= 6) return [1, 3, 6];
  if (colorCount >= 3) return [1, 3];
  return [1];
}

/**
 * Whether a product can be built into a bundle — it offers a pack size beyond 1,
 * either from its own price rule or from having enough colors to mix and match.
 * @param {any} product
 */
function isBundleEligible(product) {
  return getAllowedBundleQuantities(product).length > 1;
}

function loadBundleProductsFromCatalog() {
  try {
    const catalogEl = $("#bundle-products-catalog");
    if (!catalogEl) return;
    const parsed = JSON.parse(catalogEl.textContent || "{}");
    (parsed.products || []).forEach((p) => {
      if (!p?.handle) return;
      p.allowedQuantities = getAllowedBundleQuantities(p);
      if (isBundleEligible(p)) {
        BUNDLE_PRODUCTS.set(p.handle, p);
      }
    });
  } catch (_) {
    // keep existing bundle product data
  }
}
loadBundleProductsFromCatalog();
if (BUNDLE_PRODUCT) {
  BUNDLE_PRODUCT.allowedQuantities = getAllowedBundleQuantities(BUNDLE_PRODUCT);
}

/*
 * Swatch color by name, used when a product is loaded from /products/*.js —
 * that payload carries no swatch data, unlike the Liquid-rendered JSON which
 * uses the merchant's swatch. Values are sampled from the product photography
 * and mirror snippets/imanuel-swatch-hex.liquid; keep the two in sync.
 */
function colorHexForName(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("navy")) return "#27354D";
  if (n.includes("espresso") || n.includes("chocolate")) return "#503B31";
  if (n.includes("brown") || n.includes("mocha")) return "#5F5049";
  if (n.includes("stone")) return "#DBCFBA";
  if (n.includes("beige") || n.includes("sand") || n.includes("ecru")) return "#D7CDC1";
  if (n.includes("cream") || n.includes("ivory") || n.includes("off white") || n.includes("off-white")) return "#EDEBE2";
  if (n.includes("light olive")) return "#8C8C6F";
  if (n.includes("olive") || n.includes("khaki")) return "#6B7052";
  if (n.includes("charcoal")) return "#3D3D3D";
  if (n.includes("light grey") || n.includes("light gray") || n.includes("heather")) return "#C9C7C4";
  if (n.includes("dark grey") || n.includes("dark gray")) return "#5C5C5C";
  // "Grey" is the Relaxed Fit Tee's charcoal, "Gray" the neutral mid-grey.
  if (n.includes("grey")) return "#57555A";
  if (n.includes("gray")) return "#A8A7A6";
  if (n.includes("black")) return "#1C1C1C";
  if (n.includes("white")) return "#EAE9EA";
  if (n.includes("blue")) return "#B4C4E2";
  if (n.includes("green")) return "#4F6B4A";
  if (n.includes("burgundy") || n.includes("wine")) return "#6B2B2B";
  if (n.includes("red") || n.includes("pink")) return "#EFAEBC";
  if (n.includes("tan") || n.includes("camel")) return "#C4A574";
  return "#C9C2B6";
}

function isLightSwatchHex(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 200;
}

function normalizeProductJson(prod) {
  const options = prod.options || [];
  const variants = prod.variants || [];
  let colorIdx = -1;
  let sizeIdx = -1;
  options.forEach((name, i) => {
    const n = String(name || "").toLowerCase();
    if (n.includes("color") || n.includes("colour")) colorIdx = i;
    if (n.includes("size")) sizeIdx = i;
  });
  if (colorIdx < 0 && sizeIdx < 0) return null;

  const colorsMap = new Map();
  const sizesSet = new Set();
  variants.forEach((v) => {
    const color = [v.option1, v.option2, v.option3][colorIdx];
    const size = sizeIdx >= 0 ? [v.option1, v.option2, v.option3][sizeIdx] : null;
    if (size) sizesSet.add(size);
    if (!color) return;
    if (!colorsMap.has(color)) {
      const hex = colorHexForName(color);
      colorsMap.set(color, { name: color, hex, available: !!v.available, image: v.featured_image?.src || null });
    } else {
      const entry = colorsMap.get(color);
      if (v.available) entry.available = true;
      if (!entry.image && v.featured_image?.src) entry.image = v.featured_image.src;
    }
  });

  const normalized = /** @type {any} */ ({
    id: prod.id,
    title: prod.title,
    handle: prod.handle,
    url: `/products/${prod.handle}`,
    price_cents: typeof prod.price === "number" ? prod.price : null,
    unit: "Tee",
    image: prod.featured_image || variants[0]?.featured_image?.src || null,
    options: {
      colors: Array.from(colorsMap.values()),
      sizes: Array.from(sizesSet).map((name) => ({ name, available: true })),
    },
    variants: variants.map((v) => ({
      id: v.id,
      available: !!v.available,
      inventory_quantity: v.inventory_quantity,
      inventory_management: v.inventory_management || null,
      title: v.title,
      options: [v.option1, v.option2, v.option3].filter(Boolean),
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      image: v.featured_image?.src || null,
    })),
  });
  normalized.allowedQuantities = getAllowedBundleQuantities(normalized);
  return normalized;
}

/* Bundle UI: prefer the open quick-add modal buy box; otherwise the
   page-level one (never a closed modal leftover on the homepage).

   The buy box is a <div class="imanuel-bundle-inline" data-bundle-inline>, but
   its sibling <script id="bundle-catalog" data-bundle-inline="true"> ALSO carries
   the attribute. A <script> has no .bundle-piece children, so if it wins here the
   pieces never expand in the quick-add modal (only Tee 1 stays selectable). Always
   resolve to the real inline div, never the script. */
/** @param {ParentNode} root */
function pickBundleInline(root) {
  return root.querySelector(".imanuel-bundle-inline[data-bundle-inline]")
    || root.querySelector("[data-bundle-inline]:not(script)")
    || root.querySelector("[data-bundle-inline]");
}
function activeBundleHost() {
  const dialog = document.querySelector("dialog.quick-add-modal");
  if (dialog?.open) {
    return pickBundleInline(dialog);
  }
  const all = [...$$("[data-bundle-inline]:not(script)")].filter(
    (el) => !el.closest("dialog.quick-add-modal")
  );
  return all.length ? all[all.length - 1] : null;
}

/** @param {string} sel */
function bq(sel) {
  const host = activeBundleHost();
  if (host) {
    if (typeof host.matches === "function" && host.matches(sel)) return host;
    const inside = host.querySelector(sel);
    if (inside) return inside;
    // #bundle-catalog is a sibling of the inline div, not a descendant — scope to
    // the shared parent so we get THIS product's catalog, not the homepage one.
    const sibling = host.parentElement?.querySelector(sel);
    if (sibling) return sibling;
  }
  // Fallback: last match outside a closed quick-add dialog
  const els = [...$$(sel)].filter((el) => {
    const dlg = el.closest("dialog.quick-add-modal");
    return !dlg || dlg.open;
  });
  return els.length ? els[els.length - 1] : null;
}
/** @returns {any[]} */
function bundleTierButtons() {
  const group = bq("#bundle-tiers");
  return group ? [...group.querySelectorAll(".tier")] : [];
}
function bundlePiecesRoot() {
  return bq("#bundle-pieces");
}

let bundlePicks = []; // [{color, size, variantId, image}]
let activePieceIndex = 0;
let bundleQty = 1; // order quantity for single-tier (non-bundle) products
let bundleAdding = false; // guards against double add-to-cart (double/long press)

function bundleHasColors() {
  return (BUNDLE_PRODUCT?.options?.colors || []).length > 0;
}
function bundleHasSizes() {
  return (BUNDLE_PRODUCT?.options?.sizes || []).length > 0;
}
/** Single-piece mode: product has no bundle tiers, quantity comes from the stepper. */
function bundleSingleMode() {
  const allowed = BUNDLE_PRODUCT?.allowedQuantities || getAllowedBundleQuantities(BUNDLE_PRODUCT);
  return allowed.length === 1;
}

/**
 * Always use the Piece 1 / Color & size card — including single-color
 * and size-only products (same UI as multi-color).
 */
function usesHorizonVariantPicker() {
  return false;
}

function bundleInlineRoot() {
  return activeBundleHost();
}

/** Sync piece 0 from the visible Horizon size/color radios (PDP or quick-add). */
function syncPicksFromHorizonPicker() {
  if (!BUNDLE_PRODUCT || !usesHorizonVariantPicker()) return false;
  const root = bundleInlineRoot()?.closest(".product-details, .quick-add-modal__content, #quick-add-modal-content")
    || document;
  const picker = root.querySelector("variant-picker") || document.querySelector("variant-picker");
  if (!picker) return false;

  let color = null;
  let size = null;
  let variantId = null;
  picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
    const fieldset = input.closest("fieldset");
    const legend = (fieldset?.querySelector("legend")?.textContent || "").toLowerCase();
    const name = (input.name || "").toLowerCase();
    const val = input.value;
    if (legend.includes("color") || legend.includes("colour") || name.includes("color") || name.includes("colour")) {
      color = val;
    } else if (legend.includes("size") || name.includes("size")) {
      size = val;
    } else if (!bundleHasColors() && bundleHasSizes()) {
      size = size || val;
    }
    if (input.dataset.variantId) variantId = +input.dataset.variantId;
  });

  const colors = BUNDLE_PRODUCT.options?.colors || [];
  if (!color && colors.length === 1) color = colors[0].name;

  let v = variantId
    ? (BUNDLE_PRODUCT.variants || []).find((x) => x.id === variantId) || null
    : null;
  if (!v) {
    v = findVariant(color, size)
      || (size ? findVariant(null, size) : null)
      || (color ? findVariant(color, null) : null);
  }
  if (v) {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    if (!size && bundleHasSizes()) {
      size = opts.find((o) => (BUNDLE_PRODUCT.options.sizes || []).some((s) => (s.name || s) === o)) || size;
    }
    if (!color && bundleHasColors()) {
      color = opts.find((o) => (BUNDLE_PRODUCT.options.colors || []).some((c) => c.name === o)) || color;
    }
  }

  bundlePicks[0] = {
    color: color || null,
    size: size || null,
    variantId: v?.id || variantId || null,
    image: v?.image || getColorMeta(color)?.image || BUNDLE_PRODUCT.image || null,
  };
  return true;
}

function bindHorizonVariantSync() {
  if (document.documentElement.dataset.bundleHorizonSync === "1") return;
  document.documentElement.dataset.bundleHorizonSync = "1";
  document.addEventListener("change", (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (!(t instanceof HTMLInputElement) || t.type !== "radio") return;
    if (!t.closest("variant-picker")) return;
    if (!usesHorizonVariantPicker()) return;
    syncPicksFromHorizonPicker();
    renderBundle();
  });
}

/* Rewrite the tier buttons' prices for the selected product. */
function updateBundleTierButtons() {
  if (!BUNDLE_PRODUCT) return;
  bundleTierButtons().forEach((btn) => {
    const qty = +btn.dataset.qty;
    const t = bundleTierFor(BUNDLE_PRODUCT, qty);
    if (!t.base) return;
    btn.dataset.price = String(t.price);
    btn.dataset.compare = String(t.compare);
    const priceEl = btn.querySelector("small");
    if (priceEl) priceEl.textContent = moneyAmt(t.price);
    const eachEl = btn.querySelector(".tier__each");
    if (eachEl) eachEl.textContent = tf(I18Nb().each, "{price} each", { price: moneyAmt(t.each) });
  });
}

function applyAllowedBundleTiers() {
  const allowed = BUNDLE_PRODUCT?.allowedQuantities || getAllowedBundleQuantities(BUNDLE_PRODUCT);
  updateBundleTierButtons();
  const tiers = bundleTierButtons();
  tiers.forEach((btn) => {
    const qty = +btn.dataset.qty;
    const ok = allowed.includes(qty);
    btn.hidden = !ok;
    btn.disabled = !ok;
  });
  const active = tiers.find((b) => b.classList.contains("is-active"));
  if (!active || active.hidden) {
    // Preselect the smallest discounted pack (3 for most products, 2 for the
    // Striped Pants) rather than the single piece.
    const visible = tiers.filter((b) => !b.hidden);
    const preferred = visible.find((b) => +b.dataset.qty > 1) || visible[0];
    tiers.forEach((x) => x.classList.remove("is-active"));
    if (preferred) preferred.classList.add("is-active");
  }
  // Single-piece products: swap the tier row for the quantity stepper.
  const single = allowed.length === 1;
  const tierStep = bq("#bundle-tier-step");
  if (tierStep) tierStep.hidden = single;
  const qtyRow = bq("#bundle-qtyrow");
  if (qtyRow) qtyRow.hidden = !single;
  if (!single) bundleQty = 1;
  const qtyNum = bq("#bundle-qty-num");
  if (qtyNum) qtyNum.textContent = String(bundleQty);

  const root = bundleInlineRoot();
  if (root) {
    root.classList.toggle("imanuel-bundle-inline--simple", single);
    root.toggleAttribute("data-bundle-simple", single);
    root.classList.toggle("imanuel-bundle-inline--horizon", usesHorizonVariantPicker());
  }
  const piecesStep = bq("#bundle-pieces-step");
  if (piecesStep) piecesStep.hidden = usesHorizonVariantPicker();
}

function bundleUnitName(product) {
  const t = String(product?.title || "").toLowerCase();
  if (t.includes("hoodie")) return "Hoodie";
  if (t.includes("crew")) return "Crewneck";
  if (t.includes("pant") || t.includes("trouser") || t.includes("jogger") || t.includes("short")) return "Piece";
  if (t.includes("tee") || t.includes("t-shirt") || t.includes("tank")) return "Tee";
  return product?.unit || "Piece";
}

function shortProductTitle(title) {
  return String(title || "").replace(/^IMANU[ÉE]L\s+/i, "").trim() || title;
}

function updateBundleSelectTrigger() {
  const label = $("#bundle-product-name");
  const thumb = $("#bundle-select-thumb");
  const link = $(".bundle__product-link");
  if (!BUNDLE_PRODUCT) return;
  if (label) label.textContent = shortProductTitle(BUNDLE_PRODUCT.title);
  if (link) link.setAttribute("href", BUNDLE_PRODUCT.url || "#");
  if (thumb) {
    const src = BUNDLE_PRODUCT.image || BUNDLE_PRODUCT.options?.colors?.[0]?.image || "";
    thumb.innerHTML = src ? `<img src="${src}" alt="">` : "";
  }
}

function closeBundleSelect() {
  const menu = $("#bundle-product-picker");
  const trigger = $("#bundle-product-trigger");
  if (menu) menu.hidden = true;
  if (trigger) trigger.setAttribute("aria-expanded", "false");
}

function openBundleSelect() {
  const menu = $("#bundle-product-picker");
  const trigger = $("#bundle-product-trigger");
  if (menu) menu.hidden = false;
  if (trigger) trigger.setAttribute("aria-expanded", "true");
}

function toggleBundleSelect() {
  const menu = $("#bundle-product-picker");
  if (!menu) return;
  if (menu.hidden) openBundleSelect();
  else closeBundleSelect();
}

function selectBundleProduct(p) {
  if (!p) return;
  BUNDLE_PRODUCT = p;
  bundlePicks = [];
  activePieceIndex = 0;
  bundleQty = 1;
  applyAllowedBundleTiers();
  updateBundleSelectTrigger();
  setBundleStageImage(p.image || p.options?.colors?.[0]?.image || "", p.title);
  renderPieces();
  renderBundle();
  renderBundleProductPicker();
  closeBundleSelect();
}

function renderBundleProductPicker() {
  const root = $("#bundle-product-picker");
  if (!root) return;
  const list = Array.from(BUNDLE_PRODUCTS.values())
    .filter(isBundleEligible)
    .sort((a, b) => a.title.localeCompare(b.title));
  root.innerHTML = "";
  list.forEach((p) => {
    const li = document.createElement("li");
    li.setAttribute("role", "option");
    li.setAttribute("aria-selected", BUNDLE_PRODUCT?.handle === p.handle ? "true" : "false");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "bundle-select__option" + (BUNDLE_PRODUCT?.handle === p.handle ? " is-active" : "");
    const imgSrc = p.image || p.options?.colors?.[0]?.image || "";
    btn.innerHTML = `${imgSrc ? `<img src="${imgSrc}" alt="">` : ""}<span>${shortProductTitle(p.title)}</span>`;
    btn.title = p.title;
    btn.addEventListener("click", () => selectBundleProduct(p));
    li.appendChild(btn);
    root.appendChild(li);
  });
  updateBundleSelectTrigger();
}

function collectBundleProductHandles() {
  const handles = new Set();
  if (BUNDLE_PRODUCT?.handle) handles.add(BUNDLE_PRODUCT.handle);
  try {
    const handlesEl = $("#bundle-product-handles");
    if (handlesEl) {
      JSON.parse(handlesEl.textContent || "[]").forEach((h) => {
        if (h) handles.add(h);
      });
    }
  } catch (_) {
    // ignore malformed handle list
  }
  $$("#best-sellers .product[data-id]").forEach((el) => {
    if (el.dataset.id) handles.add(el.dataset.id);
  });
  return Array.from(handles);
}

async function loadBundleProductsFromPage() {
  loadBundleProductsFromCatalog();
  const toLoad = collectBundleProductHandles().filter((h) => !BUNDLE_PRODUCTS.has(h));
  await Promise.all(toLoad.map(async (h) => {
    try {
      const res = await fetch(`/products/${h}.js`);
      if (!res.ok) return;
      const productJson = await res.json();
      const norm = normalizeProductJson(productJson);
      if (!norm) return;
      if (!isBundleEligible(norm)) return;
      BUNDLE_PRODUCTS.set(norm.handle, norm);
    } catch (_) {
      // ignore fetch errors and keep existing bundle product
    }
  }));
  if (BUNDLE_PRODUCT) {
    BUNDLE_PRODUCT.allowedQuantities = getAllowedBundleQuantities(BUNDLE_PRODUCT);
  }
  const eligible = Array.from(BUNDLE_PRODUCTS.values()).filter(isBundleEligible);
  if (eligible.length && !isBundleEligible(BUNDLE_PRODUCT)) {
    BUNDLE_PRODUCT = eligible[0];
  }
  applyPrimaryBundleProduct();
  applyAllowedBundleTiers();
  renderBundleProductPicker();
  renderPieces();
  renderBundle();
}

function getColorMeta(colorName) {
  if (!BUNDLE_PRODUCT || !colorName) return null;
  return (BUNDLE_PRODUCT.options.colors || []).find((c) => c.name === colorName) || null;
}

function isBundleInlinePdp() {
  return document.body.classList.contains("template-product")
    && $("#bundle-catalog")?.dataset?.bundleInline === "true";
}

function imageUrlKey(url) {
  if (!url) return "";
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.split("/").pop()?.split("?")[0] || "";
  } catch {
    return String(url).split("/").pop()?.split("?")[0] || "";
  }
}

/**
 * Shopify CDN image URL resized to a given width (replaces any existing width).
 * @param {string} url
 * @param {number} w
 */
function imageAtWidth(url, w) {
  if (!url) return "";
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("width", String(w));
    return u.toString();
  } catch {
    return url;
  }
}

/* Per-colour photo sets, keyed by product handle. Horizon's quick-add only
   injects the selected variant's media, so we fetch the product's full media
   (each tagged alt="color:<Name>") to show every photo of the picked colour. */
/** @type {Map<string, Record<string, string[]>>} */
const COLOR_MEDIA_CACHE = new Map();

/** @param {string} handle */
async function loadColorMedia(handle) {
  if (!handle) return {};
  const cached = COLOR_MEDIA_CACHE.get(handle);
  if (cached) return cached;
  /** @type {Record<string, string[]>} */
  const map = {};
  try {
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: "application/json" } });
    const data = await res.json();
    (data.media || []).forEach((/** @type {any} */ m) => {
      if (m.media_type !== "image") return;
      const match = /^color:(.+)$/i.exec(m.alt || "");
      if (!match) return;
      const color = match[1].trim().toLowerCase();
      const arr = map[color] || (map[color] = []);
      arr.push(m.src);
    });
  } catch (e) {
    /* leave map empty — gallery falls back to Horizon's default */
  }
  COLOR_MEDIA_CACHE.set(handle, map);
  return map;
}

/**
 * Quick-add modal: show every photo of the picked colour — stacked on desktop,
 * a swipe carousel on mobile. Falls back to Horizon's gallery if none are tagged.
 * @param {Element} modal
 * @param {string} colorName
 */
function renderModalColorGallery(modal, colorName) {
  const media = modal.querySelector(".product-information__media");
  if (!media) return;
  const handle = BUNDLE_PRODUCT?.handle;
  if (!handle) return;

  const cached = COLOR_MEDIA_CACHE.get(handle);
  if (!cached) {
    loadColorMedia(handle).then(() => renderModalColorGallery(modal, colorName));
    return;
  }
  const imgs = cached[colorName.trim().toLowerCase()] || [];
  if (!imgs.length) return; // no tagged photos for this colour — keep default gallery

  let stack = media.querySelector(".imanuel-qa-colorgallery");
  if (!stack) {
    stack = document.createElement("div");
    stack.className = "imanuel-qa-colorgallery";
    media.appendChild(stack);
  }
  stack.innerHTML = imgs
    .map((src) => `<img src="${imageAtWidth(src, 900)}" alt="${colorName}" loading="eager">`)
    .join("");
  // Hide Horizon's native gallery so only the picked colour's photos show.
  media.querySelectorAll("media-gallery, slideshow-component").forEach((el) => {
    /** @type {HTMLElement} */ (el).style.display = "none";
  });
  media.scrollTop = 0;
}

/** Sync the visible gallery to the picked colour (modal shows all photos of it; PDP drives the picker). */
function syncBundleColorToProductGallery(colorName) {
  if (!colorName) return;

  const host = activeBundleHost();
  const modal = host && host.closest ? host.closest("dialog.quick-add-modal") : null;
  if (modal) {
    renderModalColorGallery(modal, colorName);
    return;
  }

  if (!isBundleInlinePdp()) return;

  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const colorFieldset = picker?.querySelector(".variant-option--swatches");
  if (colorFieldset) {
    const input = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'))
      .find((el) => el.value === colorName);
    if (input) {
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }
  }

  const imgSrc = getColorMeta(colorName)?.image;
  if (!imgSrc) return;

  const gallery = document.querySelector("media-gallery");
  const slideshow = gallery?.slideshow;
  if (!slideshow || !gallery?.media?.length) return;

  const targetKey = imageUrlKey(imgSrc);
  const idx = gallery.media.findIndex((el) => {
    const img = el.querySelector("img");
    if (!img) return false;
    const src = img.currentSrc || img.src || "";
    return imageUrlKey(src) === targetKey || src.includes(targetKey);
  });
  if (idx >= 0) slideshow.select(idx, undefined, { animate: true });
}

function getPickImageSrc(pick) {
  if (!BUNDLE_PRODUCT) return "";
  if (pick?.image) return pick.image;
  if (pick?.color) {
    const color = getColorMeta(pick.color);
    if (color?.image) return color.image;
    const variant = findVariant(pick.color, pick.size);
    if (variant?.image) return variant.image;
  }
  return BUNDLE_PRODUCT.image || "";
}

function setBundleStageImage(src, label) {
  const img = $("#bundle-media-img");
  const labelEl = $("#bundle-media-label");
  if (!img) return;
  if (labelEl) labelEl.textContent = label || BUNDLE_PRODUCT?.title || "";

  if (!src) {
    img.hidden = true;
    img.classList.remove("is-visible");
    return;
  }

  const reveal = () => {
    img.hidden = false;
    requestAnimationFrame(() => img.classList.add("is-visible"));
  };

  const current = img.getAttribute("src") || "";
  if (current === src) {
    img.alt = label || BUNDLE_PRODUCT.title;
    reveal();
    return;
  }

  img.classList.remove("is-visible");
  const onLoad = () => {
    img.removeEventListener("load", onLoad);
    reveal();
  };
  img.addEventListener("load", onLoad);
  img.src = src;
  img.alt = label || BUNDLE_PRODUCT.title;
  if (img.complete) onLoad();
}

function showBundleStage(index) {
  const b = bundleState();
  if (!b) return;
  activePieceIndex = Math.max(0, Math.min(index, b.qty - 1));
  const pick = bundlePicks[activePieceIndex];
  let label = BUNDLE_PRODUCT.title;
  if (pick?.color && pick?.size) label = `${pick.color} · ${pick.size}`;
  else if (pick?.color) label = `${pick.color} — pick a size`;
  else label = `${bundleUnitName(BUNDLE_PRODUCT)} ${activePieceIndex + 1} — choose color`;

  setBundleStageImage(getPickImageSrc(pick) || BUNDLE_PRODUCT.image, label);
  // Scope to the active buy box — homepage + modal both have .bundle-piece nodes.
  const host = bundleInlineRoot() || document;
  host.querySelectorAll(".bundle-piece").forEach((row, i) => {
    row.classList.toggle("is-active", i === activePieceIndex);
  });
  host.querySelectorAll(".bundle__preview-thumb").forEach((thumb, i) => {
    thumb.classList.toggle("is-active", i === activePieceIndex);
  });
  if (pick?.color) syncBundleColorToProductGallery(pick.color);
}

function renderBundlePreview() {
  const root = bq("#bundle-preview");
  const b = bundleState();
  if (!root || !b) return;
  root.innerHTML = "";

  for (let i = 0; i < b.qty; i++) {
    const pick = bundlePicks[i];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "bundle__preview-thumb" + (i === activePieceIndex ? " is-active" : "");
    btn.setAttribute("aria-label", `Preview ${bundleUnitName(BUNDLE_PRODUCT)} ${i + 1}`);

    const src = getPickImageSrc(pick);
    if (src && pick?.color) {
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.loading = "lazy";
      btn.appendChild(img);
    } else if (pick?.color) {
      const color = getColorMeta(pick.color);
      btn.style.background = color?.hex || "#C9C2B6";
    } else {
      btn.classList.add("is-empty");
    }

    const num = document.createElement("span");
    num.textContent = String(i + 1);
    btn.appendChild(num);

    btn.addEventListener("click", () => {
      showBundleStage(i);
      bundlePiecesRoot()?.querySelector(`.bundle-piece[data-index="${i}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    root.appendChild(btn);
  }
}

function findVariant(color, size) {
  if (!BUNDLE_PRODUCT || !BUNDLE_PRODUCT.variants) return null;
  return BUNDLE_PRODUCT.variants.find((v) => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    const hasColor = !color || opts.includes(color);
    const hasSize = !size || opts.includes(size);
    return hasColor && hasSize;
  }) || null;
}

function variantPickCounts(excludeIndex = -1) {
  const counts = new Map();
  bundlePicks.forEach((p, i) => {
    if (i === excludeIndex || !p?.variantId) return;
    counts.set(p.variantId, (counts.get(p.variantId) || 0) + 1);
  });
  return counts;
}

function variantMaxSelectable(v) {
  if (!v || !v.available) return 0;
  if (v.inventory_management !== "shopify") return Number.POSITIVE_INFINITY;
  // Storefront /products/*.js and Liquid often omit or zero-out inventory_quantity
  // even when the variant is available. Never treat that as sold out.
  if (v.inventory_quantity == null || v.inventory_quantity === "") {
    return Number.POSITIVE_INFINITY;
  }
  const qty = Number(v.inventory_quantity);
  if (!Number.isFinite(qty)) return Number.POSITIVE_INFINITY;
  if (qty <= 0 && v.available) return Number.POSITIVE_INFINITY;
  return Math.max(0, qty);
}

function remainingVariantStock(color, size, pieceIndex) {
  const v = findVariant(color, size);
  if (!v) return 0;
  const max = variantMaxSelectable(v);
  if (!Number.isFinite(max)) return Number.POSITIVE_INFINITY;
  const used = variantPickCounts(pieceIndex).get(v.id) || 0;
  return Math.max(0, max - used);
}

function canSelectVariantForPiece(color, size, pieceIndex) {
  if (bundleHasColors() && !color) return false;
  if (bundleHasSizes() && !size) return false;
  return remainingVariantStock(color, size, pieceIndex) > 0;
}

function canSelectColorForPiece(color, pieceIndex) {
  if (!color || !BUNDLE_PRODUCT) return false;
  const sizes = BUNDLE_PRODUCT.options?.sizes || [];
  if (sizes.some((s) => {
    const name = typeof s === "string" ? s : s.name;
    return canSelectVariantForPiece(color, name, pieceIndex);
  })) {
    return true;
  }
  // Fallback: any in-stock variant of this color (covers odd size labeling)
  const used = variantPickCounts(pieceIndex);
  return (BUNDLE_PRODUCT.variants || []).some((v) => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    if (!opts.includes(color) || !v.available) return false;
    const max = variantMaxSelectable(v);
    if (!Number.isFinite(max)) return true;
    return max - (used.get(v.id) || 0) > 0;
  });
}

function validateBundlePicks(qty) {
  for (let i = 0; i < qty; i++) {
    const p = bundlePicks[i];
    if (!p) continue;
    if (p.color && !canSelectColorForPiece(p.color, i)) {
      bundlePicks[i] = { color: null, size: null, variantId: null, image: null };
      continue;
    }
    if (p.size && !canSelectVariantForPiece(p.color, p.size, i)) {
      const colorMeta = getColorMeta(p.color);
      bundlePicks[i] = {
        color: p.color,
        size: null,
        variantId: null,
        image: colorMeta?.image || null,
      };
    }
  }
}

function bundleState() {
  const tier = bq("#bundle-tiers")?.querySelector(".is-active");
  if (!tier || !BUNDLE_PRODUCT) return null;
  const single = bundleSingleMode();
  const qty = single ? 1 : +tier.dataset.qty;
  const t = bundleTierFor(BUNDLE_PRODUCT, qty);
  if (!t.base) {
    // Product JSON without price_cents — fall back to the tier button data.
    t.price = +tier.dataset.price || 0;
    t.compare = +tier.dataset.compare || t.price;
    t.each = qty ? Math.round(t.price / qty) : t.price;
    t.base = qty ? Math.round(t.compare / qty) : t.compare;
    t.save = Math.max(0, t.compare - t.price);
  }
  // Single-piece products multiply by the quantity stepper instead of tiers.
  const orderQty = single ? Math.max(1, bundleQty) : 1;
  return {
    product: BUNDLE_PRODUCT,
    catName: BUNDLE_PRODUCT.title,
    unit: BUNDLE_PRODUCT.unit || "Tee",
    qty,
    orderQty,
    price: Math.round(t.price * orderQty * 100) / 100,
    full: Math.round(t.compare * orderQty * 100) / 100,
    each: t.each,
    base: t.base,
    save: Math.round(t.save * orderQty * 100) / 100,
    picks: bundlePicks.slice(0, qty),
  };
}

function piecesComplete(b) {
  if (!b) return false;
  const needColor = bundleHasColors();
  const needSize = bundleHasSizes();
  if (!needColor && !needSize) return !!(b.picks[0] && b.picks[0].variantId);
  return b.picks.length === b.qty && b.picks.every((p, i) => {
    if (!p) return false;
    if (needColor && !p.color) return false;
    if (needSize && !p.size) return false;
    return canSelectVariantForPiece(p.color, p.size, i);
  });
}

function bundlePackQtyFromProps(props = {}) {
  const raw = props._bundle || props.Bundle || "";
  const match = String(raw).match(/(\d+)/);
  return match ? +match[1] : 0;
}

function bundlePieceLabels(props = {}) {
  return Object.entries(props)
    .filter(([key, val]) => /^Tee \d+$/i.test(key) && val)
    .sort(([a], [b]) => +a.replace(/\D/g, "") - +b.replace(/\D/g, ""))
    .map(([, val]) => val);
}

function isBundleLineItem(item) {
  return !!(item.properties && item.properties._bundle);
}

function isBundleGroupPiece(item) {
  const props = item.properties || {};
  return !!(props._bundle_id && props.Bundle);
}

function isLegacyBundlePiece(item) {
  const props = item.properties || {};
  return !!(props.Bundle && props.Piece && !props._bundle && !props._bundle_id);
}

/** @param {any[]} lines */
function bundleRowPriceCents(lines) {
  // Line prices already include Shopify's automatic bundle discounts.
  return lines.reduce((sum, l) => sum + l.final_line_price, 0);
}

function partitionCartItems(items) {
  const display = [];
  const used = new Set();

  items.forEach((item) => {
    if (isBundleLineItem(item)) {
      display.push({ kind: "bundle", line: item, lines: [item] });
      used.add(item.key);
    }
  });

  const byBundleId = new Map();
  items.filter((item) => !used.has(item.key) && isBundleGroupPiece(item)).forEach((item) => {
    const id = item.properties._bundle_id;
    if (!byBundleId.has(id)) byBundleId.set(id, []);
    byBundleId.get(id).push(item);
  });

  byBundleId.forEach((group) => {
    group.sort((a, b) => +a.properties.Piece - +b.properties.Piece);
    const packQty = bundlePackQtyFromProps(group[0].properties);
    display.push({ kind: "bundle-group", lines: group, pack: packQty });
    group.forEach((c) => used.add(c.key));
  });

  const legacy = items.filter((item) => !used.has(item.key) && isLegacyBundlePiece(item));
  const pools = new Map();
  legacy.forEach((item) => {
    const pack = item.properties.Bundle;
    const key = `${item.product_id}:${pack}`;
    if (!pools.has(key)) pools.set(key, []);
    pools.get(key).push(item);
  });

  pools.forEach((group) => {
    const packQty = bundlePackQtyFromProps(group[0].properties);
    if (!packQty) return;
    group.sort((a, b) => items.indexOf(a) - items.indexOf(b));
    while (group.length >= packQty) {
      const chunk = group.splice(0, packQty);
      const pieceNums = chunk.map((c) => +c.properties.Piece).sort((a, b) => a - b);
      const complete = pieceNums.length === packQty
        && pieceNums.every((n, i) => n === i + 1);
      if (!complete) {
        chunk.forEach((item) => {
          if (!used.has(item.key)) {
            display.push({ kind: "line", line: item });
            used.add(item.key);
          }
        });
        continue;
      }
      display.push({ kind: "bundle-group", lines: chunk, pack: packQty });
      chunk.forEach((c) => used.add(c.key));
    }
    group.forEach((item) => {
      if (!used.has(item.key)) {
        display.push({ kind: "line", line: item });
        used.add(item.key);
      }
    });
  });

  items.forEach((item) => {
    if (!used.has(item.key)) display.push({ kind: "line", line: item });
  });

  return display;
}

/* Units in the bag. Deliberately NOT "rows" — a bundle used to count as 1,
   which made the header read "YOUR BAG (3)" against checkout's "4 items" for
   the same cart. This mirrors Shopify's item_count so the two always agree. */
function cartDisplayCount(items) {
  return items.reduce((n, item) => n + (item?.quantity || 0), 0);
}

function computeCartDisplayTotal(items, shopCart) {
  if (shopCart && typeof shopCart.total_price === "number") {
    return shopCart.total_price;
  }
  return partitionCartItems(items).reduce((sum, row) => {
    if (row.kind === "bundle" || row.kind === "bundle-group") {
      return sum + bundleRowPriceCents(row.lines || [row.line]);
    }
    return sum + row.line.final_line_price;
  }, 0);
}

function renderBundleCartRow(row) {
  const lines = row.lines || (row.line ? [row.line] : []);
  const line = lines[0];
  if (!line) return "";
  const packQty = row.pack || bundlePackQtyFromProps(line.properties);
  const props = line.properties || {};
  /* Each piece is {label, text}. Cross-bundle lines carry their own label
     ("TOP"/"BOTTOM") and are different products, so they name themselves;
     quantity-bundle pieces stay "Tee 1…n" of one product. */
  const pieces = lines.length > 1 || props.Piece
    ? lines
      .slice()
      .sort((a, b) => +(a.properties?.Piece || 0) - +(b.properties?.Piece || 0))
      .map((l, i) => {
        const lp = l.properties || {};
        const label = lp._bundle_piece_label || `Tee ${i + 1}`;
        // A cross-bundle piece may have only one of the two (size-only bottoms).
        const detail = [lp.Color, lp.Size].filter(Boolean).join(" · ");
        if (detail) {
          return {
            label,
            text: lp._bundle_piece_label && lp._product_title
              ? `${lp._product_title} · ${detail}`
              : detail,
          };
        }
        const teeKey = Object.keys(lp).find((k) => /^Tee \d+$/i.test(k));
        return { label, text: teeKey ? lp[teeKey] : l.variant_title };
      })
    : bundlePieceLabels(props).map((text, i) => ({ label: `Tee ${i + 1}`, text }));
  const title = props._bundle_title || props._product_title || line.product_title || BUNDLE_PRODUCT?.title || "Bundle";
  const packLabel = props._bundle_label || (packQty === 1 ? "Single" : `${packQty}-Pack`);
  const priceCents = bundleRowPriceCents(lines);
  const img = line.image
    ? `<img src="${line.image}" alt="${title}" loading="lazy" width="64" height="80">`
    : "";
  const removeKeys = lines.map((l) => cartLineKey(l)).filter(Boolean);
  const keysJson = JSON.stringify(removeKeys).replace(/'/g, "&#39;");
  const piecesHtml = pieces.length
    ? `<ul class="cart-item__bundle-pieces">${pieces.map((p) => `<li><span>${p.label}</span> ${p.text}</li>`).join("")}</ul>`
    : "";

  return `
    <div class="cart-item cart-item--bundle" data-bundle-keys='${keysJson}'>
      <div class="cart-item__thumb">${img}</div>
      <div class="cart-item__body">
        <div class="cart-item__top">
          <p>${title} · ${packLabel}</p>
          <p>${moneyFromCents(priceCents)}</p>
        </div>
        ${piecesHtml}
        <div class="cart-item__controls cart-item__controls--bundle">
          <span class="cart-item__bundle-lock">Bundle · Qty 1</span>
          <button type="button" class="cart-item__remove" data-remove-bundle>REMOVE BUNDLE</button>
        </div>
      </div>
    </div>`;
}

function renderPieces() {
  const piecesRoot = bundlePiecesRoot();
  if (!piecesRoot || !BUNDLE_PRODUCT) return;
  const b = bundleState();
  if (!b) return;

  if (usesHorizonVariantPicker()) {
    syncPicksFromHorizonPicker();
    piecesRoot.innerHTML = "";
    renderBundlePreview();
    return;
  }

  const colors = BUNDLE_PRODUCT.options.colors || [];
  const sizes = BUNDLE_PRODUCT.options.sizes || [];
  const unit = bundleUnitName(BUNDLE_PRODUCT);

  // Products with no color/size options: auto-select the first available variant.
  if (!colors.length && !sizes.length) {
    const v = (BUNDLE_PRODUCT.variants || []).find((/** @type {any} */ x) => x.available) || null;
    bundlePicks = [{ color: null, size: null, variantId: v ? v.id : null, image: v?.image || null }];
    piecesRoot.innerHTML = "";
    renderBundlePreview();
    return;
  }

  const next = [];
  for (let i = 0; i < b.qty; i++) {
    next[i] = bundlePicks[i] || { color: null, size: null, variantId: null };
    // Single-color products: pre-select the only color so sizes show immediately
    if (colors.length === 1 && !next[i].color) {
      const only = colors[0];
      if (canSelectColorForPiece(only.name, i)) {
        next[i] = {
          color: only.name,
          size: next[i].size || null,
          variantId: null,
          image: only.image || null,
        };
      }
    }
  }
  bundlePicks = next;
  activePieceIndex = Math.min(activePieceIndex, Math.max(0, b.qty - 1));
  validateBundlePicks(b.qty);

  piecesRoot.innerHTML = "";
  for (let i = 0; i < b.qty; i++) {
    const pick = bundlePicks[i];
    const variant = pick.size ? findVariant(pick.color, pick.size) : null;
    const pickValid = canSelectVariantForPiece(pick.color, pick.size, i);
    const row = document.createElement("div");
    row.className = "bundle-piece"
      + (pickValid ? " is-complete" : "")
      + (i === activePieceIndex ? " is-active" : "");
    row.dataset.index = String(i);
    row.addEventListener("click", (e) => {
      if (e.target.closest("button")) return;
      showBundleStage(i);
    });

    const head = document.createElement("div");
    head.className = "bundle-piece__head";
    let summary = "";
    const pickedLabel = [pick.color, pick.size].filter(Boolean).join(" · ");
    if (pick.size && (pick.color || !colors.length)) {
      summary = pickValid
        ? `<span class="bundle-piece__summary is-done">${pickedLabel}</span>`
        : `<span class="bundle-piece__summary is-soldout">${pickedLabel} — coming soon</span>`;
    } else if (pick.color) {
      summary = `<span class="bundle-piece__summary">${pick.color} — pick size</span>`;
    }
    head.innerHTML = `<span class="bundle-piece__num">${i + 1}</span><span class="bundle-piece__label">${unit} ${i + 1}</span>${summary}`;
    row.appendChild(head);

    const colorRow = document.createElement("div");
    colorRow.className = "bundle-piece__colors";
    colorRow.setAttribute("role", "group");
    colorRow.setAttribute("aria-label", `${unit} ${i + 1} color`);
    colors.forEach((c) => {
      const btn = document.createElement("button");
      const colorOk = canSelectColorForPiece(c.name, i);
      btn.type = "button";
      btn.className = "bundle-swatch"
        + (pick.color === c.name ? " is-active" : "")
        + (!colorOk ? " is-soldout" : "");
      btn.title = colorOk ? c.name : `${c.name} — coming soon`;
      btn.setAttribute("aria-label", colorOk ? c.name : `${c.name} — coming soon`);
      btn.style.background = c.hex || "#C9C2B6";
      if (isLightSwatchHex(c.hex || "#C9C2B6")) btn.dataset.light = "true";
      btn.disabled = !colorOk;
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!canSelectColorForPiece(c.name, i)) return;
        const keptSize = canSelectVariantForPiece(c.name, bundlePicks[i].size, i) ? bundlePicks[i].size : null;
        const v = keptSize ? findVariant(c.name, keptSize) : null;
        bundlePicks[i] = {
          color: c.name,
          size: keptSize,
          variantId: v ? v.id : null,
          image: (v && v.image) || c.image || null,
        };
        activePieceIndex = i;
        syncBundleColorToProductGallery(c.name);
        renderPieces();
        renderBundle();
      });
      colorRow.appendChild(btn);
    });
    if (colors.length) row.appendChild(colorRow);

    const sizeRow = document.createElement("div");
    sizeRow.className = "bundle-piece__sizes";
    sizeRow.setAttribute("role", "group");
    sizeRow.setAttribute("aria-label", `${unit} ${i + 1} size`);
    sizes.forEach((s) => {
      const name = typeof s === "string" ? s : s.name;
      const needsColor = colors.length > 0 && !pick.color;
      const sizeOk = needsColor ? false : canSelectVariantForPiece(pick.color, name, i);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bundle-size"
        + (pick.size === name ? " is-active" : "")
        + (!sizeOk && !needsColor ? " is-soldout" : "")
        + (needsColor ? " is-pending" : "");
      btn.textContent = name;
      btn.disabled = !sizeOk;
      btn.title = needsColor ? "Pick a color first" : (sizeOk ? name : `${name} — coming soon`);
      btn.setAttribute("aria-label", needsColor ? `${name} — pick a color first` : (sizeOk ? name : `${name} — coming soon`));
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!canSelectVariantForPiece(bundlePicks[i].color, name, i)) return;
        const v = findVariant(bundlePicks[i].color, name);
        bundlePicks[i] = {
          color: bundlePicks[i].color,
          size: name,
          variantId: v ? v.id : null,
          image: (v && v.image) || (getColorMeta(bundlePicks[i].color)?.image) || null,
        };
        activePieceIndex = i;
        renderPieces();
        renderBundle();
      });
      sizeRow.appendChild(btn);
    });
    row.appendChild(sizeRow);

    piecesRoot.appendChild(row);
  }

  renderBundlePreview();
  showBundleStage(activePieceIndex);
}

function setBundleAddLabel(text) {
  const addBtn = bq("#bundle-add");
  if (!addBtn) return;
  const label = addBtn.querySelector(".bundle-add-btn__label");
  if (label) label.textContent = text;
  else addBtn.textContent = text;
}

function renderBundle() {
  const b = bundleState();
  if (!b) return;
  const priceEl = bq("#bundle-price");
  if (!priceEl) return;

  if (usesHorizonVariantPicker()) syncPicksFromHorizonPicker();

  priceEl.textContent = moneyAmt(b.price);
  const compareEl = bq("#bundle-compare");
  const saveEl = bq("#bundle-save");
  if (compareEl) {
    compareEl.textContent = b.save ? moneyAmt(b.full) : "";
    compareEl.hidden = !b.save;
  }
  if (saveEl) {
    saveEl.textContent = b.save ? tf(I18Nb().save, "SAVE {price}", { price: moneyAmt(b.save) }) : "";
    saveEl.hidden = !b.save;
  }

  const eachEl = bq("#bundle-each");
  const simple = bundleSingleMode();
  if (eachEl) {
    if (simple && !b.save) {
      eachEl.hidden = true;
    } else {
      eachEl.hidden = false;
      eachEl.textContent = b.qty > 1 && b.save
        ? tf(I18Nb().each_vs_single, "{price} each (vs {base} single)", { price: moneyAmt(b.each), base: moneyAmt(b.base) })
        : tf(I18Nb().each, "{price} each", { price: moneyAmt(b.each) });
    }
  }

  const priceBlock = bq("#bundle-price-block");
  if (priceBlock) priceBlock.hidden = simple && !b.save;

  const badge = bq("#bundle-badge-pill");
  if (badge) {
    badge.hidden = !b.save;
    if (b.save) badge.textContent = tf(I18Nb().save, "SAVE {price}", { price: moneyAmt(b.save) });
  }

  const noteEl = bq("#bundle-note");
  if (noteEl) noteEl.hidden = !b.save;

  const unit = bundleUnitName(BUNDLE_PRODUCT);
  const unitLower = unit.toLowerCase();
  const piecesLabel = bq("#bundle-pieces-label");
  if (piecesLabel) {
    piecesLabel.textContent = simple
      ? (bundleHasColors() ? tt(I18Nb().color_and_size, "Color & size") : tt(I18Nb().size, "Size"))
      : tt(I18Nb().configure_each_piece, "Configure each piece");
  }

  const hint = bq("#bundle-pieces-hint");
  const done = piecesComplete(b);
  if (hint) {
    hint.textContent = done
      ? tf(I18Nb().all_ready, "All {count} {unit} ready.", { count: b.qty, unit: `${unitLower}${b.qty > 1 ? "s" : ""}` })
      : "";
    hint.hidden = !done || simple;
  }

  const addBtn = bq("#bundle-add");
  if (addBtn) {
    if (!done) {
      addBtn.disabled = true;
      if (simple) {
        const pick = bundlePicks[0];
        const sold = pick?.size && bundleHasSizes() && !canSelectVariantForPiece(pick.color, pick.size, 0);
        setBundleAddLabel(sold
          ? tt(I18Nb().sold_out, "COMING SOON")
          : (bundleHasColors() ? tt(I18Nb().select_color_size, "SELECT COLOR & SIZE") : tt(I18Nb().select_size, "SELECT SIZE")));
      } else {
        setBundleAddLabel(bundleHasColors()
          ? tt(I18Nb().select_color_size, "SELECT COLOR & SIZE")
          : (bundleHasSizes() ? tt(I18Nb().select_size, "SELECT SIZE") : tt(I18Nb().unavailable, "UNAVAILABLE")));
      }
    } else if (simple) {
      const nItems = b.qty * (b.orderQty || 1);
      addBtn.disabled = false;
      setBundleAddLabel(nItems > 1
        ? tf(I18Nb().add_n_price, "ADD {count} — {price}", { count: nItems, price: moneyAmt(b.price) })
        : tf(I18Nb().add_to_bag_price, "ADD TO BAG — {price}", { price: moneyAmt(b.price) }));
    } else {
      const nItems = b.qty * (b.orderQty || 1);
      addBtn.disabled = false;
      setBundleAddLabel(tf(I18Nb().add_n_units, "ADD {count} {unit} — {price}", { count: nItems, unit: `${unit.toUpperCase()}${nItems > 1 && !unit.endsWith("s") ? "S" : ""}`, price: moneyAmt(b.price) }));
    }
  }
}

function applyPrimaryBundleProduct() {
  const primary = bq("#bundle-catalog")?.dataset?.primaryHandle;
  if (primary && BUNDLE_PRODUCTS.has(primary)) {
    BUNDLE_PRODUCT = BUNDLE_PRODUCTS.get(primary);
    BUNDLE_PRODUCT.allowedQuantities = getAllowedBundleQuantities(BUNDLE_PRODUCT);
    updateBundleSelectTrigger();
    setBundleStageImage(BUNDLE_PRODUCT.image || BUNDLE_PRODUCT.options?.colors?.[0]?.image || "", BUNDLE_PRODUCT.title);
  }
}

function seedBundlePickFromSelectedVariant() {
  if (!BUNDLE_PRODUCT) return;
  if (usesHorizonVariantPicker()) {
    syncPicksFromHorizonPicker();
    return;
  }
  if (bundlePicks[0]?.color) return;

  // Prefer the picker inside the open quick-add modal, then PDP.
  const scope =
    document.querySelector("dialog.quick-add-modal[open]")
    || document.getElementById("quick-add-modal-content")
    || document;
  const checked =
    scope.querySelector('variant-picker .variant-option--swatches input[type="radio"]:checked')
    || document.querySelector(
      'variant-picker[data-template-product-match="true"] .variant-option--swatches input[type="radio"]:checked'
    );
  const colorName = checked?.value;
  if (!colorName || !canSelectColorForPiece(colorName, 0)) return;
  const colorMeta = getColorMeta(colorName);
  bundlePicks[0] = {
    color: colorName,
    size: null,
    variantId: null,
    image: colorMeta?.image || null,
  };
}

function initBundleSection() {
  const tiers = bundleTierButtons();
  const addBtn = bq("#bundle-add");
  if (!tiers.length || !addBtn) return;

  loadBundleProductsFromCatalog();
  applyPrimaryBundleProduct();
  if (!BUNDLE_PRODUCT) return;

  applyAllowedBundleTiers();
  bindHorizonVariantSync();
  seedBundlePickFromSelectedVariant();
  if (usesHorizonVariantPicker()) syncPicksFromHorizonPicker();
  const selectTrigger = $("#bundle-product-trigger");
  if (selectTrigger && !selectTrigger.dataset.bound) {
    selectTrigger.dataset.bound = "1";
    selectTrigger.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleBundleSelect();
    });
  }
  if (!document.documentElement.dataset.bundleSelectBound) {
    document.documentElement.dataset.bundleSelectBound = "1";
    addEventListener("click", (e) => {
      if (!e.target.closest("#bundle-select")) closeBundleSelect();
    });
    addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeBundleSelect();
    });
  }
  tiers.forEach((t) => {
    if (t.dataset.bound) return;
    t.dataset.bound = "1";
    t.addEventListener("click", () => {
      tiers.forEach((x) => x.classList.remove("is-active"));
      t.classList.add("is-active");
      renderPieces();
      renderBundle();
    });
  });
  const qtyRow = bq("#bundle-qtyrow");
  if (qtyRow && !qtyRow.dataset.bound) {
    qtyRow.dataset.bound = "1";
    qtyRow.querySelector("[data-bqty-dec]")?.addEventListener("click", () => {
      bundleQty = Math.max(1, bundleQty - 1);
      applyAllowedBundleTiers();
      renderBundle();
    });
    qtyRow.querySelector("[data-bqty-inc]")?.addEventListener("click", () => {
      bundleQty = Math.min(99, bundleQty + 1);
      applyAllowedBundleTiers();
      renderBundle();
    });
  }
  if (!addBtn.dataset.bound) {
    addBtn.dataset.bound = "1";
    addBtn.addEventListener("click", async () => {
    // Hard guard: one click (or a double/long press) adds exactly once.
    if (bundleAdding) return;
    const b = bundleState();
    if (!b || !piecesComplete(b)) {
      toast(tf(I18Nb().pick_every, "Pick color & size for every {unit}", { unit: bundleUnitName(BUNDLE_PRODUCT).toLowerCase() }));
      return;
    }

    const picks = b.picks.map((p, i) => {
      const v = findVariant(p.color, p.size);
      const id = p.variantId || (v && v.id);
      if (!id || !canSelectVariantForPiece(p.color, p.size, i)) return null;
      return { ...p, variantId: id, index: i };
    }).filter(Boolean);

    if (picks.length !== b.qty) {
      toast(tt(I18Nb().pieces_sold_out, "One or more pieces are coming soon — pick a different color or size"));
      renderPieces();
      renderBundle();
      return;
    }

    const bundleId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `b${Date.now()}`;

    bundleAdding = true;
    const addBtn = bq("#bundle-add");
    if (addBtn) addBtn.disabled = true;
    try {
      // Real variants only — Shopify's automatic quantity discount reprices
      // them in cart/checkout, and inventory deducts per piece.
      const items = picks.map((p) => ({
        id: p.variantId,
        quantity: b.qty > 1 ? 1 : (b.orderQty || 1),
        properties: b.qty > 1
          ? {
            Bundle: `${b.qty}-pack`,
            Piece: String(p.index + 1),
            Color: p.color,
            Size: p.size,
            _bundle_id: bundleId,
          }
          : undefined,
      }));
      await shopifyAddItems(items);
      toast(b.save ? tf(I18Nb().added_pack, "Added {count}-pack to bag — {price}", { count: b.qty, price: moneyAmt(b.price) }) : tt(I18Nb().added_to_bag, "Added to bag"));
      // Added once — close the popup, then open the cart.
      if (typeof window.imanuelCloseQuickAdd === "function") window.imanuelCloseQuickAdd();
      openOverlay("cart");
    } catch (err) {
      toast(err.message || tt(I18Nb().could_not_add, "Could not add to bag"));
    } finally {
      bundleAdding = false;
      if (addBtn) addBtn.disabled = !piecesComplete(bundleState());
    }
    });
  }
  renderBundleProductPicker();
  renderPieces();
  renderBundle();
  if (!bq("#bundle-catalog")?.dataset?.bundleInline) {
    loadBundleProductsFromPage();
  }
}

initBundleSection();

/* Horizon's quick-add ("Choose") modal fetches product-page HTML and injects
   it — including our bundle buy box — after this script has run. Watch for
   those injections (and removals) and re-bind the bundle UI against the
   newest instance in the DOM. */
function reinitInjectedBundle() {
  const host = activeBundleHost();
  const catalogEl = host?.querySelector("#bundle-catalog") || bq("#bundle-catalog");
  if (!catalogEl) return;

  try {
    const parsed = JSON.parse(catalogEl.textContent || "{}");
    const prod = parsed.product;
    if (prod?.handle) {
      prod.allowedQuantities = getAllowedBundleQuantities(prod);
      BUNDLE_PRODUCTS.set(prod.handle, prod);
      BUNDLE_PRODUCT = prod;
    }
  } catch (_) {
    // keep the current bundle product
  }
  if (!BUNDLE_PRODUCT) return;
  // Warm the per-colour photo sets so the desktop gallery updates instantly on pick.
  loadColorMedia(BUNDLE_PRODUCT.handle);
  bundlePicks = [];
  activePieceIndex = 0;
  bundleQty = 1;
  initBundleSection();
}

window.imanuelReinitBundle = reinitInjectedBundle;

let bundleInjectTimer = 0;
new MutationObserver((muts) => {
  let shouldReinit = false;
  let injectedInModal = false;
  for (const m of muts) {
    for (const n of m.addedNodes) {
      if (n.nodeType !== 1) continue;
      const el = /** @type {Element} */ (n);
      if (
        (el.matches && (el.matches("[data-bundle-inline]") || el.matches("#bundle-catalog"))) ||
        (el.querySelector && el.querySelector("[data-bundle-inline], #bundle-catalog"))
      ) {
        shouldReinit = true;
        if (el.closest?.("dialog.quick-add-modal") || el.querySelector?.("dialog.quick-add-modal")) {
          injectedInModal = true;
        }
        if (document.getElementById("quick-add-modal-content")?.contains(el)
          || (el.id === "quick-add-modal-content")) {
          injectedInModal = true;
        }
        break;
      }
    }
    if (shouldReinit) break;
  }
  if (!shouldReinit) return;
  clearTimeout(bundleInjectTimer);
  bundleInjectTimer = window.setTimeout(() => {
    const dialog = document.querySelector("dialog.quick-add-modal");
    // Morph happens before showDialog — skip until the modal is open so we
    // don't rebind the homepage Bundle & Save against the wrong product.
    if (injectedInModal && dialog && !dialog.open) return;
    reinitInjectedBundle();
  }, 50);
}).observe(document.body, { childList: true, subtree: true });
/* ---------- best sellers (static grid) ---------- */
/* Auto: reorder by Shopify collection sort_by=best-selling (not products.json — that ignores sort) */
(async function orderBestSellersBySales() {
  const section = document.querySelector("[data-imanuel-bestsellers]");
  if (!section || section.dataset.bsAuto !== "true") return;
  const grid = section.querySelector("[data-bs-grid]");
  if (!grid) return;

  const limit = Math.max(1, Number(section.dataset.bsLimit || 6));
  const collection = section.dataset.bsCollection || "all";

  try {
    const res = await fetch(
      `/collections/${encodeURIComponent(collection)}?sort_by=best-selling&view=bestsellers-json`
    );
    if (!res.ok) throw new Error("bestsellers view failed");
    const handles = (await res.json()).filter(Boolean).slice(0, limit);
    if (!handles.length) throw new Error("empty bestsellers");

    const items = [...grid.querySelectorAll("[data-bs-item]")];
    const byHandle = new Map(items.map((el) => [el.dataset.bsItem, el]));

    handles.forEach((handle) => {
      const el = byHandle.get(handle);
      if (el) grid.appendChild(el);
    });

    items.forEach((el) => {
      if (!handles.includes(el.dataset.bsItem)) el.remove();
    });
  } catch (_) {
    const extras = [...grid.querySelectorAll("[data-bs-item]")].slice(limit);
    extras.forEach((el) => el.remove());
  } finally {
    section.setAttribute("data-bs-ready", "true");
  }
})();

/* Swatch interaction: update image, price, links, quick-add variant */
$$("[data-bs-swatch]").forEach((swatch) => {
  swatch.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const card = swatch.closest(".product");
    if (!card) return;

    card.querySelectorAll("[data-bs-swatch]").forEach((s) => s.classList.remove("is-active"));
    swatch.classList.add("is-active");

    const url = swatch.dataset.variantUrl || card.dataset.url;
    const img = swatch.dataset.image;
    const price = swatch.dataset.price;
    const priceRaw = swatch.dataset.priceRaw;
    const variantId = swatch.dataset.variantId;

    if (img) {
      const media = card.querySelector("[data-bs-media]");
      if (media) {
        let image = media.querySelector("img");
        if (!image) {
          image = document.createElement("img");
          image.loading = "lazy";
          image.alt = card.dataset.name || "";
          media.appendChild(image);
        }
        image.src = img;
        image.srcset = "";
      }
      card.dataset.image = img;
    }

    const priceEl = card.querySelector("[data-bs-price]");
    if (priceEl && price) priceEl.textContent = price;
    if (priceRaw) card.dataset.price = priceRaw;

    if (url) {
      card.dataset.url = url;
      const mediaLink = card.querySelector(".product__media-link");
      if (mediaLink) mediaLink.href = url;
      const titleLink = card.querySelector("[data-bs-title-link]");
      if (titleLink) titleLink.href = url;
    }

    const qa = card.querySelector("[data-quick-add]");
    if (qa && variantId) qa.dataset.variantId = variantId;
    const qaFormId = card.querySelector('quick-add-component input[name="id"]');
    if (qaFormId instanceof HTMLInputElement && variantId) qaFormId.value = variantId;
  });
});

/* Clicking card chrome (outside buttons/links) goes to PDP */
$$("#best-sellers .product").forEach((card) => {
  card.addEventListener("click", (e) => {
    if (e.target.closest("button, a, [data-bs-swatch], quick-add-component")) return;
    const url = card.dataset.url;
    if (url) window.location.href = url;
  });
});

/* ---------- overlays: open/close helpers ---------- */
const overlays = {
  search: $("#search"),
  cart: $("#cart"),
  quickadd: $("#quickadd"),
  shop: $("#shop"),
  bundlemenu: $("#bundlemenu"),
};
const BOTTOM_NAV_SHEETS = new Set(["shop", "search", "cart", "bundlemenu"]);
function setBottomNavActive(name) {
  $$(".bottom-nav a, .bottom-nav button").forEach((el) => el.classList.remove("is-active"));
  if (BOTTOM_NAV_SHEETS.has(name)) {
    $(`.bottom-nav [data-open="${name}"]`)?.classList.add("is-active");
  }
}
function openOverlay(name) {
  Object.values(overlays).forEach((o) => { if (o) o.hidden = true; });
  const panel = overlays[name];
  if (!panel) return;
  panel.hidden = false;
  document.body.style.overflow = "hidden";
  setBottomNavActive(name);
  if (name === "search") setTimeout(() => $("#search-input")?.focus(), 50);
  if (name === "cart") renderCart();
}
function closeOverlays() {
  closeQaSizeChart();
  Object.values(overlays).forEach((o) => { if (o) o.hidden = true; });
  document.body.style.overflow = "";
  setBottomNavActive(null);
}
$$("[data-open]").forEach((b) => b.addEventListener("click", (e) => {
  e.preventDefault();
  openOverlay(b.dataset.open);
}));
$$("[data-close]").forEach((b) => b.addEventListener("click", closeOverlays));
addEventListener("keydown", (e) => { if (e.key === "Escape") closeOverlays(); });
$$("#shop .shop-sheet__nav a, #bundlemenu .shop-sheet__nav a").forEach((a) =>
  a.addEventListener("click", closeOverlays));

/* ---------- quick add ---------- */
let qaProduct = null, qaSize = null, qaColor = null, qaVariantId = null;
/** Upsell % live for the open quick-add sheet (0 when the deal doesn't apply). */
let qaDealPercent = 0;
/** @type {HTMLElement | null} */
let qaCard = null;
const qaConfirm = $("#qa-confirm");
const qaColorsRoot = $("#qa-colors");
const qaSizesRoot = $("#qa-sizes");
const qaViewProduct = $("#qa-view-product");
const qaSizeChartBtn = $("#qa-size-chart-btn");
const qaSizeChartDrawer = $("#qa-sizechart-drawer");
const qaSizeChartOverlay = $("#qa-sizechart-overlay");
const qaSizeChartBody = $("#qa-sizechart-body");
let qaSizeChartUnit = "cm";

function closeQaSizeChart() {
  qaSizeChartOverlay?.classList.remove("is-open");
  qaSizeChartDrawer?.classList.remove("is-open");
  document.body.classList.remove("sizechart-locked");
  setTimeout(() => {
    if (qaSizeChartOverlay) qaSizeChartOverlay.hidden = true;
    if (qaSizeChartDrawer) qaSizeChartDrawer.hidden = true;
  }, 320);
}

function openQaSizeChart() {
  if (!qaSizeChartOverlay || !qaSizeChartDrawer) return;
  qaSizeChartOverlay.hidden = false;
  qaSizeChartDrawer.hidden = false;
  requestAnimationFrame(() => {
    qaSizeChartOverlay.classList.add("is-open");
    qaSizeChartDrawer.classList.add("is-open");
    document.body.classList.add("sizechart-locked");
  });
}

function qaCmToIn(numStr) {
  const n = parseFloat(numStr) / 2.54;
  return (Math.round(n * 10) / 10).toString().replace(/\.0$/, "");
}

function renderQaSizeChartTable(unit) {
  if (!qaSizeChartBody) return;
  qaSizeChartBody.querySelectorAll("[data-cm-text]").forEach((cell) => {
    const orig = cell.dataset.cmText;
    if (unit === "in") {
      cell.textContent = orig.replace(/\d+(\.\d+)?/g, (m) => qaCmToIn(m));
    } else {
      cell.textContent = orig;
    }
  });
  qaSizeChartBody.querySelectorAll("[data-unit-toggle] button").forEach((btn) => {
    btn.classList.toggle("is-active", btn.dataset.unit === unit);
  });
}

function renderQaSizeChart(product) {
  const chart = product?.sizeChart;
  const hasChart = !!(chart?.image || chart?.table?.rows?.length);
  if (qaSizeChartBtn) qaSizeChartBtn.hidden = !hasChart;
  if (!qaSizeChartBody || !hasChart) {
    if (qaSizeChartBody) qaSizeChartBody.innerHTML = "";
    return;
  }

  let html = "";
  if (chart.image) {
    html += `<img src="${chart.image}" alt="${product.name || "Product"} size chart" loading="lazy">`;
  }
  if (chart.table?.rows?.length) {
    html += `<div class="sizechart__toolbar"><div class="sizechart__toggle" data-unit-toggle role="group" aria-label="Units">
      <button type="button" data-unit="cm" class="is-active">cm</button>
      <button type="button" data-unit="in">inch</button>
    </div></div>`;
    html += `<div class="qa-sizechart-scroll"><table class="qa-sizechart-table"><thead><tr>`;
    (chart.table.headers || []).forEach((h) => { html += `<th>${h}</th>`; });
    html += `</tr></thead><tbody>`;
    chart.table.rows.forEach((row) => {
      html += "<tr>";
      row.forEach((cell, idx) => {
        if (idx === 0) html += `<th>${cell}</th>`;
        else html += `<td data-cm-text="${String(cell).replace(/"/g, "&quot;")}">${cell}</td>`;
      });
      html += "</tr>";
    });
    html += `</tbody></table></div>`;
    if (chart.table.guide?.length) {
      html += `<div class="sizechart__guide"><h4>Measurement guide</h4><dl>`;
      chart.table.guide.forEach((g) => { html += `<dt>${g[0]}</dt><dd>${g[1]}</dd>`; });
      html += `</dl></div>`;
    }
  } else if (!chart.image) {
    html += `<p class="qa-sizechart-empty">No size chart available for this product.</p>`;
  }

  qaSizeChartBody.innerHTML = html;
  qaSizeChartUnit = "cm";
  renderQaSizeChartTable("cm");
  qaSizeChartBody.querySelectorAll("[data-unit-toggle] button").forEach((btn) => {
    btn.addEventListener("click", () => {
      qaSizeChartUnit = btn.dataset.unit || "cm";
      renderQaSizeChartTable(qaSizeChartUnit);
    });
  });
}

function setupQaActions(product, card) {
  const url = product?.url || card?.dataset?.url || "#";
  if (qaViewProduct) {
    qaViewProduct.href = url;
    qaViewProduct.hidden = !url || url === "#";
  }
  renderQaSizeChart(product);
}

if (qaSizeChartBtn) qaSizeChartBtn.addEventListener("click", openQaSizeChart);
$("#qa-sizechart-close")?.addEventListener("click", closeQaSizeChart);
qaSizeChartOverlay?.addEventListener("click", closeQaSizeChart);
addEventListener("keydown", (e) => {
  if (e.key === "Escape" && qaSizeChartDrawer?.classList.contains("is-open")) closeQaSizeChart();
});

function qaVariantFor(color, size) {
  if (!qaProduct?.variants?.length) return null;
  return qaProduct.variants.find((v) => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    const hasColor = !color || opts.includes(color);
    const hasSize = !size || opts.includes(size);
    return hasColor && hasSize;
  }) || null;
}

function renderQaSizes() {
  if (!qaSizesRoot || !qaProduct) return;
  const sizes = qaProduct.sizes || [];
  qaSizesRoot.innerHTML = "";
  sizes.forEach((size) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = size;
    if (qaSize === size) btn.classList.add("is-active");
    const v = qaVariantFor(qaColor, size);
    if (!v || v.available === false) btn.classList.add("is-soldout");
    btn.addEventListener("click", () => {
      qaSize = size;
      const picked = qaVariantFor(qaColor, qaSize);
      if (!picked || picked.available === false) {
        qaVariantId = null;
        qaConfirm.disabled = true;
        qaConfirm.textContent = tt(I18N.quick_add && I18N.quick_add.unavailable, "UNAVAILABLE");
      } else {
        qaVariantId = picked.id;
        qaConfirm.disabled = false;
        // Match the discounted header price, so the button never contradicts it.
        const pickedCents = picked.price || 0;
        const btnCents = qaDealPercent
          ? Math.round((pickedCents * (100 - qaDealPercent)) / 100)
          : pickedCents;
        qaConfirm.textContent = tf(I18N.quick_add && I18N.quick_add.add_to_bag_price, "ADD TO BAG — {price}", { price: moneyFromCents(btnCents) });
      }
      renderQaSizes();
    });
    qaSizesRoot.appendChild(btn);
  });
}

function qaImageForColor(colorName) {
  if (!qaProduct) return "";
  const colorMeta = (qaProduct.colors || []).find((c) => c.name === colorName);
  if (colorMeta?.image) return colorMeta.image;
  const v = qaVariantFor(colorName, null);
  return v?.image || qaProduct.image || "";
}

function setQaImage(src) {
  const qaImg = $("#qa-image");
  if (!qaImg) return;
  if (src) {
    qaImg.src = src;
    qaImg.alt = qaProduct?.name || "";
    qaImg.hidden = false;
  } else {
    qaImg.removeAttribute("src");
    qaImg.hidden = true;
  }
}

function renderQaColors() {
  if (!qaColorsRoot || !qaProduct) return;
  qaColorsRoot.innerHTML = "";
  (qaProduct.colors || []).forEach((color) => {
    const btn = document.createElement("button");
    const hex = color.hex || colorHexForName(color.name);
    btn.type = "button";
    btn.className = "swatch" + (qaColor === color.name ? " is-active" : "") + (!color.available ? " is-unavailable" : "");
    btn.style.background = hex;
    if (isLightSwatchHex(hex)) btn.dataset.light = "true";
    btn.title = color.name;
    btn.setAttribute("aria-label", color.name);
    btn.addEventListener("click", () => {
      qaColor = color.name;
      setQaImage(qaImageForColor(qaColor));
      renderQaColors();
      renderQaSizes();
      qaVariantId = null;
      qaConfirm.disabled = true;
      qaConfirm.textContent = tt(I18N.quick_add && I18N.quick_add.select_size, "SELECT SIZE");
    });
    qaColorsRoot.appendChild(btn);
  });
}

function parseQaProduct(card) {
  const jsonEl = $(".qa-product-json", card);
  let parsed = null;
  try { parsed = JSON.parse(jsonEl?.textContent || "{}"); } catch (_) { parsed = null; }
  const variants = parsed?.variants || [];
  const colorsFromJson = parsed?.colors || [];
  const colorsMap = new Map();
  const sizeSet = new Set();

  if (colorsFromJson.length) {
    colorsFromJson.forEach((c) => colorsMap.set(c.name, { ...c, hex: c.hex || colorHexForName(c.name) }));
  }

  variants.forEach((v) => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    const color = opts.find((o) => /(black|grey|gray|white|navy|brown|beige|cream|ivory|olive|khaki|blue|green|red|pink|tan|camel|off white|off-white|charcoal|heather|espresso|mocha|sand|ecru)/i.test(String(o))) || v.option1;
    const size = opts.find((o) => /^(xxs|xs|s|m|l|xl|2xl|3xl|4xl)$/i.test(String(o)));
    if (size) sizeSet.add(size);
    if (!color) return;
    if (!colorsMap.has(color)) {
      colorsMap.set(color, {
        name: color,
        available: !!v.available,
        hex: colorHexForName(color),
        image: v.image || "",
      });
    } else {
      const entry = colorsMap.get(color);
      if (v.available) entry.available = true;
      if (!entry.image && v.image) entry.image = v.image;
    }
  });

  const defaultImage = card.dataset.image || "";
  return {
    id: card.dataset.id,
    name: card.dataset.name,
    price: +card.dataset.price,
    url: parsed?.url || card.dataset.url || "",
    sizeChart: parsed?.sizeChart || null,
    image: defaultImage,
    variants: variants.map((v) => ({
      id: v.id,
      available: v.available,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      price: v.price,
      image: v.image || defaultImage,
    })),
    colors: Array.from(colorsMap.values()),
    sizes: Array.from(sizeSet),
  };
}

/**
 * Normalize a Shopify product JSON payload (/products/<handle>.js or
 * /recommendations/products.js) into the shape the quick-add sheet expects.
 * @param {any} p
 */
function qaProductFromProductJson(p) {
  /** @type {any[]} */
  const variants = p?.variants || [];
  const colorsMap = new Map();
  const sizeSet = new Set();
  const defaultImage = (typeof p?.featured_image === "string" && p.featured_image)
    || p?.featured_image?.src
    || p?.images?.[0]
    || "";

  variants.forEach((v) => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    const vImage = (v.featured_image && (v.featured_image.src || v.featured_image)) || "";
    const color = opts.find((o) => /(black|grey|gray|white|navy|brown|beige|cream|ivory|olive|khaki|blue|green|red|pink|tan|camel|off white|off-white|charcoal|heather|espresso|mocha|sand|ecru)/i.test(String(o)))
      || (opts.length > 1 ? v.option1 : null);
    const size = opts.find((o) => /^(xxs|xs|s|m|l|xl|2xl|3xl|4xl|xxl|one size)$/i.test(String(o)));
    if (size) sizeSet.add(size);
    if (!color) return;
    if (!colorsMap.has(color)) {
      colorsMap.set(color, {
        name: color,
        available: !!v.available,
        hex: colorHexForName(color),
        image: typeof vImage === "string" ? vImage : "",
      });
    } else {
      const entry = colorsMap.get(color);
      if (v.available) entry.available = true;
      if (!entry.image && typeof vImage === "string" && vImage) entry.image = vImage;
    }
  });

  return {
    id: String(p?.id || ""),
    handle: p?.handle || "",
    name: p?.title || "",
    price: (p?.price || 0) / 100,
    url: p?.url || (p?.handle ? `/products/${p.handle}` : ""),
    sizeChart: null,
    image: defaultImage,
    variants: variants.map((v) => ({
      id: v.id,
      available: v.available,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      price: v.price,
      image: (v.featured_image && (v.featured_image.src || v.featured_image)) || defaultImage,
    })),
    colors: Array.from(colorsMap.values()),
    sizes: Array.from(sizeSet),
  };
}

/**
 * Open the quick-add sheet for a normalized product (from a page card or fetched JSON).
 * @param {ReturnType<typeof parseQaProduct>} product
 * @param {string} [fallbackImage]
 */
function openQuickAdd(product, fallbackImage) {
  qaProduct = product;
  qaSize = null;
  qaVariantId = null;
  qaColor = product.colors[0]?.name || null;
  $("#qa-name").textContent = product.name;
  const qaCents = Math.round((product.price || 0) * 100);
  qaDealPercent = 0;
  $("#qa-price").innerHTML = moneyAmt(product.price);
  setQaImage(qaImageForColor(qaColor) || fallbackImage || product.image || "");
  renderQaColors();
  renderQaSizes();
  setupQaActions(product, qaCard);
  qaConfirm.disabled = true;
  qaConfirm.textContent = tt(I18N.quick_add && I18N.quick_add.select_size, "SELECT SIZE");
  openOverlay("quickadd");
}

$$("[data-quick-add]").forEach((btn) => btn.addEventListener("click", () => {
  const card = btn.closest("[data-id]");
  if (!card) return;
  qaCard = card;
  const mediaImg = card.querySelector("[data-bs-media] img");
  const cardSrc = (mediaImg && mediaImg.currentSrc) || (mediaImg && mediaImg.src) || card.dataset.image || "";
  openQuickAdd(parseQaProduct(card), cardSrc);
}));

/* Use Horizon's full quick-add modal on all screens (same buy box as desktop).
   Mobile used to open a stripped IMANUEL sheet because Horizon removed product-details —
   that strip is disabled in quick-add.js so the Choose button can open the real modal. */

qaConfirm.addEventListener("click", async () => {
  if (!qaProduct || !qaSize || !qaVariantId) return;
  try {
    await shopifyAddItems([{ id: +qaVariantId, quantity: 1 }]);
    closeOverlays();
    toast(tf(I18Nb().added_to_bag_named, "Added to bag — {name}", { name: qaProduct.name }));
    openOverlay("cart");
  } catch (err) {
    toast(err.message || tt(I18Nb().could_not_add, "Could not add to bag"));
  }
});

/* ---------- mobile sticky add-to-cart (product pages) ---------- */
/* Scrolls to the buy box (bundle tiers / piece picker) and flashes it;
   falls back to the quick-add sheet if no buy box is on the page. */
const stickyAtc = $("#sticky-atc");
if (stickyAtc) {
  $("#sticky-atc-btn")?.addEventListener("click", async (/** @type {Event} */ e) => {
    const buyBox = document.querySelector("[data-bundle-inline], .buy-buttons-block");
    if (buyBox) {
      buyBox.scrollIntoView({ behavior: "smooth", block: "center" });
      buyBox.classList.remove("buybox-flash");
      void (/** @type {any} */ (buyBox)).offsetWidth;
      buyBox.classList.add("buybox-flash");
      return;
    }
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    btn.disabled = true;
    try {
      const res = await fetch(`/products/${stickyAtc.dataset.handle}.js`);
      if (!res.ok) throw new Error("Could not load product");
      const norm = qaProductFromProductJson(await res.json());
      qaCard = null;
      openQuickAdd(norm, norm.image);
    } catch (err) {
      toast(err.message || "Could not load product");
    } finally {
      btn.disabled = false;
    }
  });
}

/* ---------- cart (Shopify) ---------- */
function updateBadges(count) {
  const n = count ?? 0;
  $$('[data-badge="cart"]').forEach((/** @type {any} */ b) => {
    const prev = b.textContent;
    b.hidden = n === 0;
    b.textContent = n;
    if (!b.hidden && prev !== String(n)) {
      // restart the pop animation on count change
      b.classList.remove("badge-pop");
      void b.offsetWidth;
      b.classList.add("badge-pop");
    }
  });
  const inline = $('[data-badge="cart-inline"]');
  if (inline) inline.textContent = n;
}

/* ---------- cart conversion: shipping bar · bundle nudge · recommendations ---------- */
function cartConversionConfig() {
  return /** @type {any} */ (window).IMANUEL_CART || {};
}

/**
 * @param {string} url
 * @param {number} width
 */
function cdnResize(url, width) {
  if (!url) return "";
  return url + (url.includes("?") ? "&" : "?") + "width=" + width;
}

/**
 * "You're $X away from free shipping" progress bar in the bag.
 * @param {boolean} hasItems
 * @param {number} totalCents
 */
function renderShippingBar(hasItems, totalCents) {
  const bar = $("#cart-shipbar");
  if (!bar) return;
  const threshold = Math.max(0, Number(cartConversionConfig().freeShippingThreshold) || 0);
  if (!threshold || !hasItems) {
    bar.hidden = true;
    bar.dataset.unlocked = "0";
    return;
  }
  const remaining = threshold - totalCents;
  const pct = Math.max(4, Math.min(100, (totalCents / threshold) * 100));
  const unlocked = remaining <= 0;
  const wasUnlocked = bar.dataset.unlocked === "1";
  bar.hidden = false;
  bar.classList.toggle("is-unlocked", unlocked);
  if (unlocked && !wasUnlocked) {
    // restart the one-shot celebration animation
    bar.classList.remove("just-unlocked");
    void bar.offsetWidth;
    bar.classList.add("just-unlocked");
  }
  bar.dataset.unlocked = unlocked ? "1" : "0";
  const msgEl = $("#cart-shipbar-msg");
  if (msgEl) {
    msgEl.innerHTML = remaining > 0
      ? `You're <b>${moneyFromCents(remaining)}</b> away from <b>free shipping</b>`
      : `<b>Free shipping unlocked</b> — delivery is on us ✓`;
  }
  const pctEl = $("#cart-shipbar-pct");
  if (pctEl) pctEl.textContent = `${Math.round(pct)}%`;
  const fill = $("#cart-shipbar-fill");
  if (fill) fill.style.width = pct.toFixed(1) + "%";
}

/**
 * "Add X more, pay less per piece" nudge for products covered by the
 * Bundle & Save automatic discounts (see BUNDLE_TIER_PRODUCT_IDS).
 * @param {any[]} items
 */
function renderBundleNudge(items) {
  const root = $("#cart-nudge");
  if (!root) return;

  const counts = new Map();
  items.forEach((item) => {
    if (!BUNDLE_TIER_PRODUCT_IDS.has(item.product_id)) return;
    const entry = counts.get(item.product_id) || { qty: 0, item };
    entry.qty += item.quantity;
    counts.set(item.product_id, entry);
  });
  if (!counts.size) {
    root.hidden = true;
    root.innerHTML = "";
    return;
  }

  // For each bundle-eligible product in the bag, find the next tier it can
  // still climb to (3-pack, then 6-pack where the rule defines one).
  const entries = [...counts.values()].map((e) => {
    const base = Math.round(e.item.price / 100);
    const tiers = bundleRuleTiers(e.item.product_id, base);
    const nextTier = tiers.find((t) => e.qty < t) || 0;
    const topTier = tiers[tiers.length - 1] || 0;
    return { ...e, base, tiers, nextTier, topTier };
  });
  const pending = entries.filter((e) => e.nextTier).sort((a, b) => b.qty - a.qty);

  if (!pending.length) {
    const unlocked = entries.find((e) => e.topTier && e.qty >= e.topTier);
    if (!unlocked) {
      root.hidden = true;
      root.innerHTML = "";
      return;
    }
    const each = bundleEachPrice(unlocked.item.product_id, unlocked.base, unlocked.topTier);
    const unit = bundleUnitName({ title: unlocked.item.product_title }).toLowerCase();
    root.innerHTML = `<p class="cart-nudge__done">Best bundle price unlocked — ${money(each)}/${unit} ✓</p>`;
    root.hidden = false;
    return;
  }

  const { qty, item, base, tiers, nextTier } = pending[0];
  const each = bundleEachPrice(item.product_id, base, nextTier);
  const currentTier = tiers.filter((/** @type {number} */ t) => qty >= t).pop() || 1;
  const eachNow = bundleEachPrice(item.product_id, base, currentTier);
  const unit = bundleUnitName({ title: item.product_title }).toLowerCase();
  const need = nextTier - qty;
  const delta = each * nextTier - eachNow * qty;
  const deltaMsg = delta <= 0
    ? `and your total <b>drops ${money(-delta)}</b>`
    : `for just <b>${money(delta)}</b> more`;

  root.innerHTML = `
    <p>Add <b>${need} more ${unit}${need > 1 ? "s" : ""}</b> ${deltaMsg} — that's ${money(each)}/${unit} instead of ${money(eachNow)}.</p>
    <button type="button" class="cart-nudge__btn" data-nudge-add>ADD ANOTHER ${unit.toUpperCase()}</button>`;
  root.hidden = false;

  $("[data-nudge-add]", root)?.addEventListener("click", async (/** @type {Event} */ e) => {
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    btn.disabled = true;
    try {
      const res = await fetch(`/products/${item.handle}.js`);
      if (!res.ok) throw new Error("Could not load product");
      const norm = qaProductFromProductJson(await res.json());
      qaCard = null;
      openQuickAdd(norm, norm.image);
    } catch (err) {
      toast(err.message || "Could not load product");
      btn.disabled = false;
    }
  });
}

const RECS_CACHE = new Map();
/** @param {number} productId */
function fetchRecommendations(productId) {
  if (!RECS_CACHE.has(productId)) {
    const promise = fetch(`/recommendations/products.js?product_id=${productId}&limit=8&intent=related`)
      .then((res) => (res.ok ? res.json() : { products: [] }))
      .then((data) => data.products || [])
      .catch(() => []);
    RECS_CACHE.set(productId, promise);
  }
  return RECS_CACHE.get(productId);
}

const COLLECTION_PRODUCTS_CACHE = new Map();
const PRODUCT_BY_HANDLE_CACHE = new Map();
/** Latest cart lines — used so cart-rail quick-add can gate the deal price. */
let qaCartItemsCache = [];

/**
 * Remember an upsell product id discovered at runtime (fallback handles).
 * @param {number|string|undefined} id
 */
function rememberUpsellId(id) {
  const n = Number(id);
  if (!n || !window.IMANUEL_CART) return;
  const ids = window.IMANUEL_CART.upsellIds || (window.IMANUEL_CART.upsellIds = []);
  if (!ids.map(Number).includes(n)) ids.push(n);
}

/**
 * Storefront products of a collection, mapped to the shape the
 * recommendations rail uses. `_needsFetch` marks that the full
 * /products/<handle>.js payload must be fetched before quick-add.
 * @param {string} handle
 * @returns {Promise<any[]>}
 */
function fetchCollectionProducts(handle) {
  if (!COLLECTION_PRODUCTS_CACHE.has(handle)) {
    const promise = fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=12`)
      .then((res) => (res.ok ? res.json() : { products: [] }))
      .then((data) => (data.products || []).map((/** @type {any} */ p) => {
        rememberUpsellId(p.id);
        return {
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          available: (p.variants || []).some((/** @type {any} */ v) => v.available !== false),
          price: Math.round(parseFloat(p.variants?.[0]?.price || "0") * 100),
          featured_image: p.images?.[0]?.src || null,
          _needsFetch: true,
        };
      }))
      .catch(() => []);
    COLLECTION_PRODUCTS_CACHE.set(handle, promise);
  }
  return COLLECTION_PRODUCTS_CACHE.get(handle);
}

/**
 * Fetch a single product by handle for the upsell rail.
 * @param {string} handle
 * @returns {Promise<any|null>}
 */
function fetchProductByHandle(handle) {
  if (!handle) return Promise.resolve(null);
  if (!PRODUCT_BY_HANDLE_CACHE.has(handle)) {
    const promise = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((res) => (res.ok ? res.json() : null))
      .then((p) => {
        if (!p) return null;
        rememberUpsellId(p.id);
        const img = p.featured_image
          || (typeof p.images?.[0] === "string" ? p.images[0] : p.images?.[0]?.src)
          || null;
        return {
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          available: (p.variants || []).some((/** @type {any} */ v) => v.available !== false),
          price: p.price || Math.round(parseFloat(p.variants?.[0]?.price || "0") * 100),
          featured_image: img,
          _needsFetch: false,
          _full: p,
        };
      })
      .catch(() => null);
    PRODUCT_BY_HANDLE_CACHE.set(handle, promise);
  }
  return PRODUCT_BY_HANDLE_CACHE.get(handle);
}

let recsToken = 0;

/**
 * Upsell rail in the bag: suggested products at full price (no discount).
 * Prefers the merchant Upsell Picks collection / fallbacks, then Shopify recommendations.
 * @param {any[]} items
 * @param {number} [totalCents] - current bag total, for the shipping goal
 */
async function renderCartRecommendations(items, totalCents = 0) {
  const root = $("#cart-recs");
  if (!root) return;
  const cfg = cartConversionConfig();
  if (cfg.showRecommendations === false || !items.length) {
    root.hidden = true;
    root.innerHTML = "";
    return;
  }

  const token = ++recsToken;
  const inCart = new Set(items.map((i) => i.product_id));
  const threshold = Math.max(0, Number(cfg.freeShippingThreshold) || 0);
  const shipRemaining = threshold > 0 ? Math.max(0, threshold - totalCents) : 0;

  const fallbackHandles = cfg.upsellFallbackHandles || [];
  const fallbackProducts = (await Promise.all(fallbackHandles.map(fetchProductByHandle)))
    .filter((p) => p && !inCart.has(p.id) && p.available !== false);
  if (token !== recsToken) return;

  let recs = [...fallbackProducts];
  if (cfg.upsellCollection) {
    const fromCol = (/** @type {any[]} */ (await fetchCollectionProducts(cfg.upsellCollection)) || [])
      .filter((p) => !inCart.has(p.id) && p.available !== false);
    if (token !== recsToken) return;
    const seen = new Set(recs.map((p) => p.id));
    recs = [...recs, ...fromCol.filter((p) => !seen.has(p.id))];
  }
  if (recs.length < 3) {
    const all = /** @type {any[]} */ ((await fetchRecommendations(items[0].product_id)) || []);
    if (token !== recsToken) return;
    const seen = new Set(recs.map((p) => p.id));
    recs = [...recs, ...all.filter((p) => !inCart.has(p.id) && p.available !== false && !seen.has(p.id))];
  }

  /** @param {any} p */
  const effPrice = (p) => p.price;

  if (shipRemaining > 0) {
    recs = recs.slice().sort((a, b) => {
      const aOver = effPrice(a) >= shipRemaining ? 1 : 0;
      const bOver = effPrice(b) >= shipRemaining ? 1 : 0;
      if (aOver !== bOver) return bOver - aOver;
      return aOver ? effPrice(a) - effPrice(b) : effPrice(b) - effPrice(a);
    });
  }

  recs = recs.slice(0, 3);
  if (!recs.length) {
    root.hidden = true;
    root.innerHTML = "";
    return;
  }

  const headHtml = shipRemaining > 0
    ? `<p class="eyebrow">COMPLETE THE LOOK</p>
       <p class="cart-recs__goal">You're <b>${moneyFromCents(shipRemaining)}</b> from <b>free shipping</b> — these get you there:</p>`
    : `<p class="eyebrow">COMPLETE THE LOOK</p>`;

  root.innerHTML = headHtml + recs.map((p, i) => {
    const img = p.featured_image
      ? `<img src="${cdnResize(p.featured_image, 120)}" alt="${p.title}" loading="lazy" width="52" height="52">`
      : "";
    const chips = [];
    if (shipRemaining > 0 && effPrice(p) >= shipRemaining) {
      chips.push(`<span class="cart-rec__deal cart-rec__deal--ship">UNLOCKS FREE SHIPPING</span>`);
    }
    return `
      <div class="cart-rec">
        <a class="cart-rec__thumb" href="${p.url}">${img}</a>
        <div class="cart-rec__info">
          <p>${p.title}</p>
          <small>${moneyFromCents(p.price)}</small>
          ${chips.length ? `<span class="cart-rec__chips">${chips.join("")}</span>` : ""}
        </div>
        <button type="button" class="cart-rec__add" data-rec-index="${i}">ADD</button>
      </div>`;
  }).join("");
  root.hidden = false;

  $$(".cart-rec__add", root).forEach((btn) => btn.addEventListener("click", async () => {
    const p = recs[+btn.dataset.recIndex];
    if (!p) return;
    btn.disabled = true;
    btn.textContent = tt(I18Nc().adding, "ADDING…");
    const resetBtn = () => {
      btn.disabled = false;
      btn.textContent = tt(I18Nc().add, "ADD");
    };
    try {
      let full = p._full || p;
      if (p._needsFetch || !full.variants) {
        const res = await fetch(`/products/${p.handle}.js`);
        if (!res.ok) throw new Error("Could not load product");
        full = await res.json();
        rememberUpsellId(full.id);
      }
      const variants = full.variants || [];
      if (variants.length === 1) {
        await shopifyAddItems([{ id: variants[0].id, quantity: 1 }]);
        toast(tf(I18Nb().added_to_bag_named, "Added to bag — {name}", { name: p.title }));
        await renderCart();
        return;
      }
      const norm = qaProductFromProductJson(full);
      if (!norm.sizes.length && !norm.colors.length) {
        window.location.href = p.url;
        return;
      }
      qaCard = null;
      openQuickAdd(norm, norm.image);
      resetBtn();
    } catch (err) {
      toast(err.message || tt(I18Nb().could_not_add, "Could not add to bag"));
      resetBtn();
    }
  }));
}

async function renderCart() {
  const body = $("#cart-items");
  const foot = $("#cart-foot");
  const checkoutBtn = $("#checkout-btn");
  let shopCart;

  try {
    shopCart = await fetchShopifyCart();
  } catch {
    if (body) body.innerHTML = `<div class="cart__empty"><p>Could not load bag.</p></div>`;
    if (foot) foot.hidden = true;
    return;
  }

  const items = shopCart.items || [];
  qaCartItemsCache = items;
  items.forEach((item) => {
    if (item?.product_id && (upsellIdSet().has(Number(item.product_id)) || isUpsellPick(item))) {
      rememberUpsellId(item.product_id);
    }
  });
  const displayTotal = computeCartDisplayTotal(items, shopCart);
  updateBadges(cartDisplayCount(items));
  const rows = partitionCartItems(items);

  renderShippingBar(items.length > 0, displayTotal);
  renderBundleNudge(items);
  renderCartRecommendations(items, displayTotal);

  if (!items.length) {
    if (foot) foot.hidden = true;
    if (checkoutBtn) {
      checkoutBtn.classList.add("is-disabled");
      checkoutBtn.setAttribute("aria-disabled", "true");
      checkoutBtn.textContent = tt(I18Nc().checkout, "CHECKOUT");
    }
    if (body) {
      body.innerHTML = `<div class="cart__empty">
        <p>Your bag is empty.</p><p>Start with the essentials.</p>
        <button class="btn btn--dark" type="button" data-close-shop>SHOP BEST SELLERS</button></div>`;
      $("[data-close-shop]", body)?.addEventListener("click", () => {
        closeOverlays();
        location.hash = "best-sellers";
        const el = $("#best-sellers");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      });
    }
    return;
  }

  if (foot) foot.hidden = false;
  if (checkoutBtn) {
    checkoutBtn.classList.remove("is-disabled");
    checkoutBtn.removeAttribute("aria-disabled");
    checkoutBtn.textContent = tf(I18Nc().checkout_total, "CHECKOUT — {price}", { price: moneyFromCents(displayTotal) });
    checkoutBtn.href = (window.IMANUEL_ROUTES && window.IMANUEL_ROUTES.checkout) || "/checkout";
  }

  if (body) {
    body.innerHTML = rows.map((row) => {
      if (row.kind === "bundle" || row.kind === "bundle-group") {
        return renderBundleCartRow(row);
      }

      const item = row.line;
      const meta = item.variant_title && item.variant_title !== "Default Title"
        ? item.variant_title
        : (item.properties && Object.entries(item.properties)
          .filter(([key, val]) => val && !key.startsWith("_"))
          .map(([, val]) => val)
          .join(" · ")) || "";
      const img = item.image
        ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" width="64" height="80">`
        : "";
      return `
    <div class="cart-item" data-key="${cartLineKey(item)}">
      <div class="cart-item__thumb">${img}</div>
      <div class="cart-item__body">
        <div class="cart-item__top"><p>${item.product_title}</p><p>${moneyFromCents(item.final_line_price)}</p></div>
        ${meta ? `<p class="cart-item__meta">${meta}</p>` : ""}
        <div class="cart-item__controls">
          <div class="qty">
            <button type="button" data-dec aria-label="Decrease">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-inc aria-label="Increase">+</button>
          </div>
          <button type="button" class="cart-item__remove" data-remove>REMOVE</button>
        </div>
      </div>
    </div>`;
    }).join("");

    $$(".cart-item--bundle", body).forEach((row) => {
      row.querySelector("[data-remove-bundle]")?.addEventListener("click", async () => {
        let keys = [];
        try {
          keys = JSON.parse(row.getAttribute("data-bundle-keys") || "[]");
        } catch {
          keys = (row.getAttribute("data-bundle-keys") || "").split(",").filter(Boolean);
        }
        try {
          await removeBundleFromCart(keys);
          await renderCart();
        } catch (err) { toast(err.message || tt(I18Nb().could_not_remove, "Could not remove bundle")); }
      });
    });

    $$(".cart-item:not(.cart-item--bundle)", body).forEach((row) => {
      const key = row.dataset.key;
      const item = items.find((i) => cartLineKey(i) === key);
      if (!item) return;
      const lineId = cartLineKey(item);
      row.querySelector("[data-inc]")?.addEventListener("click", async () => {
        try {
          await shopifyChangeLine(lineId, item.quantity + 1);
          await renderCart();
        } catch (err) { toast(err.message); }
      });
      row.querySelector("[data-dec]")?.addEventListener("click", async () => {
        try {
          await shopifyChangeLine(lineId, Math.max(0, item.quantity - 1));
          await renderCart();
        } catch (err) { toast(err.message); }
      });
      row.querySelector("[data-remove]")?.addEventListener("click", async () => {
        try {
          const id = cartLineKey(item);
          if (!id) throw new Error("Could not find item in bag");
          await shopifyChangeLine(id, 0);
          await renderCart();
        } catch (err) { toast(err.message); }
      });
    });
  }

  const savingsRow = $("#cart-savings-row");
  const savingsEl = $("#cart-savings");
  const cartDiscount = shopCart.total_discount || 0;

  if (savingsRow && savingsEl) {
    if (cartDiscount > 0) {
      savingsRow.hidden = false;
      savingsEl.textContent = `−${moneyFromCents(cartDiscount)}`;
    } else {
      savingsRow.hidden = true;
    }
  }

  const subtotalEl = $("#cart-subtotal");
  if (subtotalEl) subtotalEl.textContent = moneyFromCents(displayTotal);
}

fetchShopifyCart()
  .then((c) => updateBadges(cartDisplayCount(c.items || [])))
  .catch(() => updateBadges(0));

/* Native Horizon product forms (PDP buy buttons, quick-add fallback) dispatch
   cart:update — mirror them in the IMANUEL chrome: open the bag with the
   shipping bar / nudge / recommendations after an add, otherwise resync. */
document.addEventListener("cart:update", (e) => {
  const detail = /** @type {any} */ (e).detail || {};
  if (detail.data?.didError) return;
  if (detail.data?.source === "product-form-component") {
    openOverlay("cart");
  } else if (detail.data?.source) {
    renderCart();
  }
});

/* ---------- search (Shopify predictive search — real store products) ---------- */
const searchInput = $("#search-input"), searchResults = $("#search-results");
/** @type {AbortController | null} */
let searchAbort = null;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let searchDebounce;

/**
 * Shopify CDN image at a given width.
 * @param {string} url
 * @param {number} [w]
 */
function searchThumb(url, w = 160) {
  if (!url) return "";
  return url.includes("?") ? `${url}&width=${w}` : `${url}?width=${w}`;
}

/** @param {any} p */
function searchResultHTML(p) {
  const img = p.featured_image?.url || p.image || "";
  const price = p.price != null && p.price !== "" ? "$" + parseFloat(p.price).toFixed(2) : "";
  return `<a class="search-result" href="${p.url}">
      <span class="search-result__l">
        <span class="search-result__thumb">${img ? `<img src="${searchThumb(img)}" alt="" loading="lazy">` : ""}</span>
        <span><p>${p.title}</p></span>
      </span>
      <span class="search-result__price">${price}</span>
    </a>`;
}

/** @param {string} q */
async function runSearch(q) {
  if (!searchResults) return;
  const query = (q || "").trim();
  if (query.length < 2) {
    searchResults.innerHTML = "";
    return;
  }
  if (searchAbort) searchAbort.abort();
  searchAbort = new AbortController();
  try {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}`
      + `&resources[type]=product&resources[limit]=8`
      + `&resources[options][unavailable_products]=last`;
    const res = await fetch(url, { signal: searchAbort.signal, headers: { Accept: "application/json" } });
    const data = await res.json();
    const products = data?.resources?.results?.products || [];
    searchResults.innerHTML = products.length
      ? products.map(searchResultHTML).join("")
      : `<p class="search-empty">No results for “${query}”.</p>`;
  } catch (e) {
    if (e.name !== "AbortError") {
      searchResults.innerHTML = `<p class="search-empty">Search is unavailable right now.</p>`;
    }
  }
}

/** Default products shown before the shopper types anything. */
let trendingHTML = "";
async function loadTrending() {
  if (!searchResults) return;
  if (trendingHTML) {
    if (!searchInput.value.trim()) searchResults.innerHTML = trendingHTML;
    return;
  }
  try {
    const res = await fetch("/products.json?limit=6", { headers: { Accept: "application/json" } });
    const data = await res.json();
    const products = (data.products || []).map((/** @type {any} */ p) => ({
      title: p.title,
      url: "/products/" + p.handle,
      price: p.variants?.[0]?.price,
      image: p.images?.[0]?.src,
    }));
    trendingHTML = products.map(searchResultHTML).join("");
    if (!searchInput.value.trim()) searchResults.innerHTML = trendingHTML;
  } catch (e) {
    /* trending is optional — ignore */
  }
}

if (searchInput && searchResults) {
  searchInput.addEventListener("input", (/** @type {Event} */ e) => {
    clearTimeout(searchDebounce);
    const v = /** @type {HTMLInputElement} */ (e.target).value;
    if (!v.trim()) { loadTrending(); return; }
    searchDebounce = setTimeout(() => runSearch(v), 200);
  });
  $$("[data-search]").forEach((b) => b.addEventListener("click", () => {
    searchInput.value = b.dataset.search;
    runSearch(b.dataset.search);
  }));
  loadTrending();
}

/* ---------- newsletter ---------- */
const nlForm = $("#newsletter-form");
if (nlForm) {
  nlForm.addEventListener("submit", () => {
    toast(tt(I18N.common && I18N.common.welcome, "Welcome to Imanuel ✳"));
  });
}
const footerForm = $(".footer__form");
if (footerForm) {
  // Shopify customer form — allow native submit; toast for feedback
  footerForm.addEventListener("submit", () => toast(tt(I18N.common && I18N.common.welcome, "Welcome to Imanuel ✳")));
}

/* ---------- toast ---------- */
let toastT;
function toast(msg) {
  const el = $("#toast");
  if (!el) {
    console.log("[toast]", msg);
    return;
  }
  el.textContent = msg;
  el.hidden = false;
  clearTimeout(toastT);
  toastT = setTimeout(() => (el.hidden = true), 2400);
}

/* ---------- size chart (Horizon [data-sizechart] — PDP + quick-add) ----------
   Uses a dedicated <dialog showModal()> so it stacks above Horizon's quick-add top layer. */
(function initHorizonSizeChart() {
  if (document.documentElement.dataset.sizechartDelegated === "1") return;
  document.documentElement.dataset.sizechartDelegated = "1";

  let unit = "cm";
  /** @type {HTMLElement | null} */
  let activeRoot = null;

  function toIn(numStr) {
    const n = parseFloat(numStr) / 2.54;
    return (Math.round(n * 10) / 10).toString().replace(/\.0$/, "");
  }

  function render(drawer, nextUnit) {
    unit = nextUnit || unit;
    drawer.querySelectorAll("[data-cm-text]").forEach((c) => {
      const orig = c.dataset.cmText;
      if (unit === "in") {
        c.textContent = orig.replace(/\d+(\.\d+)?/g, (m) => toIn(m));
      } else {
        c.textContent = orig;
      }
    });
    drawer.querySelectorAll("[data-unit-toggle] button").forEach((b) => {
      b.classList.toggle("is-active", b.dataset.unit === unit);
    });
  }

  function getPortal() {
    let portal = document.getElementById("imanuel-sizechart-dialog");
    if (!(portal instanceof HTMLDialogElement)) {
      portal = document.createElement("dialog");
      portal.id = "imanuel-sizechart-dialog";
      portal.className = "imanuel-sizechart-dialog";
      portal.setAttribute("aria-label", "Size chart");
      document.body.appendChild(portal);
    }
    return portal;
  }

  function findParts(root) {
    let drawer = root.querySelector("[data-sizechart-drawer]");
    let overlay = root.querySelector("[data-sizechart-overlay]");
    if (!drawer || !overlay) {
      const id = root.dataset.scId || "";
      drawer =
        document.querySelector(`[data-sizechart-drawer][data-sc-for="${id}"]`) ||
        Array.prototype.find.call(document.querySelectorAll("[data-sizechart-drawer]"), (d) => d._scRoot === root);
      overlay =
        document.querySelector(`[data-sizechart-overlay][data-sc-for="${id}"]`) ||
        Array.prototype.find.call(document.querySelectorAll("[data-sizechart-overlay]"), (o) => o._scRoot === root);
    }
    return { drawer, overlay };
  }

  function closeChart() {
    const portal = getPortal();
    const drawer = portal.querySelector("[data-sizechart-drawer]");
    const overlay = portal.querySelector("[data-sizechart-overlay]");
    const root = activeRoot || drawer?._scRoot;
    if (portal.open) {
      try { portal.close(); } catch (_) { /* ignore */ }
    }
    document.body.classList.remove("sizechart-locked");
    if (root && root.isConnected && drawer && overlay) {
      drawer.hidden = true;
      overlay.hidden = true;
      drawer.classList.remove("is-open");
      overlay.classList.remove("is-open");
      root.appendChild(overlay);
      root.appendChild(drawer);
    } else {
      drawer?.remove();
      overlay?.remove();
    }
    activeRoot = null;
  }

  function openChart(root) {
    const { drawer, overlay } = findParts(root);
    if (!drawer) {
      console.warn("[sizechart] drawer missing");
      return;
    }

    if (!root.dataset.scId) root.dataset.scId = `sc-${Math.random().toString(36).slice(2, 9)}`;
    drawer.dataset.scFor = root.dataset.scId;
    if (overlay) {
      overlay.dataset.scFor = root.dataset.scId;
      overlay._scRoot = root;
    }
    drawer._scRoot = root;
    activeRoot = root;

    const portal = getPortal();
    /* Clear prior content */
    while (portal.firstChild) portal.removeChild(portal.firstChild);
    if (overlay) {
      overlay.hidden = true;
      portal.appendChild(overlay);
    }
    drawer.hidden = false;
    drawer.classList.add("is-open");
    portal.appendChild(drawer);

    if (!drawer.dataset.scWired) {
      drawer.dataset.scWired = "1";
      render(drawer, "cm");
      drawer.querySelectorAll("[data-unit-toggle] button").forEach((b) => {
        b.addEventListener("click", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          render(drawer, b.dataset.unit);
        });
      });
      drawer.querySelectorAll("[data-sizechart-close]").forEach((el) => {
        el.addEventListener("click", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          closeChart();
        });
      });
    }

    if (!portal.dataset.scBackdropWired) {
      portal.dataset.scBackdropWired = "1";
      portal.addEventListener("click", (ev) => {
        if (ev.target === portal) closeChart();
      });
      portal.addEventListener("cancel", (ev) => {
        ev.preventDefault();
        closeChart();
      });
    }

    document.body.classList.add("sizechart-locked");
    if (!portal.open) portal.showModal();
  }

  document.addEventListener(
    "click",
    (e) => {
      const openBtn = e.target?.closest?.("[data-sizechart-open]");
      if (!openBtn) return;
      const root = openBtn.closest("[data-sizechart]");
      if (!root) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      openChart(root);
    },
    true
  );

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const portal = document.getElementById("imanuel-sizechart-dialog");
    if (portal instanceof HTMLDialogElement && portal.open) {
      e.preventDefault();
      e.stopPropagation();
      closeChart();
    }
  }, true);
})();

/* Close Horizon quick-add on successful cart add + toast
   (display:contents on #quick-add-dialog skips bubble listeners on the host) */
(function initQuickAddCartClose() {
  function closeQuickAddModal() {
    const host = document.getElementById("quick-add-dialog");
    if (host && typeof host.closeDialog === "function") {
      const dialog = host.refs?.dialog;
      if (dialog?.open) host.closeDialog();
      return;
    }
    document.querySelector("dialog.quick-add-modal[open]")?.close?.();
  }

  document.addEventListener("cart:update", (e) => {
    if (e.detail?.data?.didError) return;
    const dialog = document.querySelector("dialog.quick-add-modal[open]");
    if (!dialog) return;
    closeQuickAddModal();
    toast(tt(I18Nb().added_to_bag, "Added to bag"));
  });

  // When the modal closes, re-bind the page buy box (homepage Bundle & Save).
  document.addEventListener("close", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLDialogElement) || !t.classList.contains("quick-add-modal")) return;
    setTimeout(() => {
      if (typeof window.imanuelReinitBundle === "function") window.imanuelReinitBundle();
    }, 0);
  }, true);

  window.imanuelCloseQuickAdd = closeQuickAddModal;
})();

/* Shared with assets/imanuel-cross-bundle.js (separate script, same UI shell) */
window.imanuelToast = toast;
window.imanuelOpenOverlay = openOverlay;

/* Re-paint dynamic money when the visitor picks a display currency */
window.addEventListener("imanuel:display-currency", () => {
  try { renderBundle(); } catch (_) {}
  try {
    if (!$("#cart")?.hidden) renderCart();
  } catch (_) {}
  window.imanuelDisplayCurrency?.convertRoot?.(document);
});
